import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import allData from "../data/allData";
import { themeContext } from '../Context';
import { auth } from '../firebase/firebaseConfig';
import QuizCategorySEO from '../utilities/SEO/QuizCategorySEO';
import SharePopup from '../utilities/SharePopup';
import SharePopupWithVideo from '../utilities/SharePopupWithVideo';
import ExplanationPopup from '../utilities/ExplanationPopup';
import { saveQuizAttempt } from '../utilities/SaveQuizAttempt';
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from '../firebase/firebaseConfig';
import { FaDownload } from 'react-icons/fa';

//SUBS
import { useSubscription } from '../utilities/SubscriptionContext';
import { FocusModePopup, FocusModeButton } from "../utilities/FocusModePopup";

const FOCUS_MODE_AUTHORIZED_EMAILS = [
    'sumansaurabh00@gmail.com',
    'swatikumari123890@gmail.com',
    'your.email@domain.com'
    // Add more emails as needed
];

function DummyPage() {
    const { quizType, quizSet } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedOptions, setSelectedOptions] = useState({});
    const [showScore, setShowScore] = useState(false);
    const [score, setScore] = useState(0);
    const [unattempted, setUnattempted] = useState(0);
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    // Focus Mode State
    const [focusMode, setFocusMode] = useState(false);
    const [focusedQuestion, setFocusedQuestion] = useState(null);

    // Language State
    const [language, setLanguage] = useState('en'); // 'en', 'hi', 'bilingual'

    // Store shuffled indices to maintain consistency across languages
    const [shuffledIndices, setShuffledIndices] = useState({});

    // State to Manage Popup Visibility
    const [showPopup, setShowPopup] = useState(false);
    const [activeQuestion, setActiveQuestion] = useState(null);
    const [activeVideoQuestion, setActiveVideoQuestion] = useState(null);
    const [showVideoPopup, setShowVideoPopup] = useState(false);

    //MARKING IMPORTANT QUESTIONS
    const [importantQuestions, setImportantQuestions] = useState([]);
    const [showOnly, setShowOnly] = useState("All");

    //Search Chips
    const [activeFilter, setActiveFilter] = useState("All");

    //SUBS
    const { isSubscriptionActive, loading } = useSubscription();

    //TAGS
    const [selectedTag, setSelectedTag] = useState("All");
    const [availableTags, setAvailableTags] = useState([]);
    const [tagSearchQuery, setTagSearchQuery] = useState('');
    const [showTagRecommendations, setShowTagRecommendations] = useState(false);

    // Explanation Declarations
    const [showExplanationPopup, setShowExplanationPopup] = useState(false);
    const [activeExplanationQuestion, setActiveExplanationQuestion] = useState(null);

    const theme = useContext(themeContext);
    const darkMode = theme.state.darkMode;

    const selectedCategory = useMemo(() => allData.find(category => category.category === quizType), [quizType]);
    const selectedSet = useMemo(() => selectedCategory?.sets.find(set => set.set === quizSet) || null, [selectedCategory, quizSet]);

    // Helper to get the original English text for a given shuffled option index
    const getEnglishTextForShuffledOption = useCallback((questionId, shuffledIndex) => {
        const originalQuestion = selectedSet?.quizzes.find(q => q.id === questionId);
        const originalEnglishOptions = originalQuestion?.options || [];
        const shuffleOrder = shuffledIndices[questionId] || [];
        const originalUnshuffledIndex = shuffleOrder[shuffledIndex];
        return originalEnglishOptions[originalUnshuffledIndex];
    }, [selectedSet, shuffledIndices]);

    // Generate consistent shuffled indices for each question
    useEffect(() => {
        if (selectedSet) {
            const indices = {};
            selectedSet.quizzes.forEach(question => {
                // Create a deterministic shuffle based on question id
                const seed = question.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                const optionsCount = question.options.length;
                const array = Array.from({ length: optionsCount }, (_, i) => i);

                // Simple deterministic shuffle
                for (let i = array.length - 1; i > 0; i--) {
                    // Use a slightly more robust deterministic shuffle if needed,
                    // but the core idea is to get a consistent order for a given seed.
                    const j = (seed + i) % (i + 1);
                    [array[i], array[j]] = [array[j], array[i]];
                }

                indices[question.id] = array;
            });
            setShuffledIndices(indices);
        }
    }, [selectedSet]);

    // Helper function to get localized question data with consistent option ordering
    const getLocalizedQuestion = useCallback((question) => {
        if (!question) return null;

        const hinQuestion = selectedSet?.quizzesHin?.find(q => q.id === question.id);
        const shuffleOrder = shuffledIndices[question.id] || [];

        // Get original (unshuffled) options arrays
        const enOptionsOriginal = question.options;
        const hiOptionsOriginal = hinQuestion?.options || [];

        // Apply same shuffle order to both language options
        const shuffledEnOptions = shuffleOrder.map(index => enOptionsOriginal[index]);
        // If Hindi translation is missing for an option, ensure it doesn't break the pairing
        const shuffledHiOptions = shuffleOrder.map(index => hiOptionsOriginal[index] || '');

        if (language === 'en') {
            return {
                ...question,
                options: shuffledEnOptions
            };
        }

        if (language === 'hi' && hinQuestion) {
            return {
                ...hinQuestion,
                options: shuffledHiOptions
            };
        }

        if (language === 'bilingual' && hinQuestion) {
            return {
                ...question, // Keep original question data as base
                question: `${question.question}\n\n${hinQuestion.question}`,
                options: shuffledEnOptions.map((enOpt, idx) => {
                    const hiOpt = shuffledHiOptions[idx];
                    return hiOpt ? `${enOpt} / ${hiOpt}` : enOpt; // Only add Hindi if available
                }),
                correctAnswer: question.correctAnswer, // Keep English for answer checking
                explanation: question.explanation
            };
        }

        // Fallback to English if Hindi not available or other issues
        return {
            ...question,
            options: shuffledEnOptions
        };
    }, [language, selectedSet, shuffledIndices]);

    // Handler to Open Popup
    const handleShareClick = (question) => {
        // Find the Hindi version of the question
        const hindiQuestion = selectedSet?.quizzesHin?.find(q => q.id === question.id);

        // Create a combined question object with both English and Hindi content
        const questionWithBothLanguages = {
            ...question,
            questionHi: hindiQuestion?.question,
            optionsHi: hindiQuestion?.options
        };

        setActiveQuestion(questionWithBothLanguages);
        setShowPopup(true);
    };

    const handleVideoShareClick = (question) => {
        setActiveVideoQuestion(question);
        setShowVideoPopup(true);
    };

    const handleExplanationClick = (question) => {
        if (!user) {
            alert("Please login to see the explanation!");
            return;
        }
        // Find the Hindi version of the question
        const hindiQuestion = selectedSet?.quizzesHin?.find(q => q.id === question.id);

        // Create a combined question object with both English and Hindi explanations
        const questionWithBothLanguages = {
            ...question,
            explanationHi: hindiQuestion?.explanation // Add Hindi explanation
        };

        setActiveExplanationQuestion(questionWithBothLanguages);
        setShowExplanationPopup(true);

    };

    // Effect to track the authentication state
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(currentUser => {
            setUser(currentUser);
            setAuthLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Determine if we should display the header based on the current path
    const showHeading = location.pathname === `/quizCategory` || location.pathname.endsWith('/quizcategory/');
    const showSubHeading = location.pathname.startsWith('/quizCategory/') && location.pathname !== '/quizCategory/';

    // Redirect if quiz set requires authentication and the user is not logged in
    useEffect(() => {
        if (!authLoading && selectedSet && selectedSet.authRequired && !user) {
            navigate('/login');
        }
    }, [selectedSet, user, navigate, authLoading]);

    useEffect(() => {
        if (loading) return;
        if (selectedSet && selectedSet.subscriptionRequired && !isSubscriptionActive) {
            alert("This premium quiz requires subscription. Redirecting to subscription page.");
            navigate('/subscription');
        }
    }, [selectedSet, isSubscriptionActive, navigate, loading]);

    // ************* MODIFIED *************
    const handleOptionClick = useCallback((questionId, shuffledIndex) => {
        if (selectedOptions[questionId] !== undefined) return; // Prevent changing selection once made

        // Get the English text of the selected option based on its shuffled index
        const selectedOptionEnglishText = getEnglishTextForShuffledOption(questionId, shuffledIndex);

        setSelectedOptions(prevOptions => ({
            ...prevOptions,
            [questionId]: selectedOptionEnglishText, // Always store the English text from the original question
        }));
    }, [selectedOptions, getEnglishTextForShuffledOption]);

    const calculateScore = async () => {
        let correctAnswers = 0;
        let unattemptedCount = 0;

        const questions = selectedSet ? selectedSet.quizzes : [];
        questions.forEach((question) => {
            const userAnswer = selectedOptions[question.id];
            // The `userAnswer` is already stored as the English text,
            // so direct comparison with `question.correctAnswer` (which should also be English) is correct.
            if (userAnswer === question.correctAnswer) {
                correctAnswers += 1;
            } else if (userAnswer === undefined) { // Check for undefined, not just falsy
                unattemptedCount += 1;
            }
        });

        const attemptData = {
            score: correctAnswers,
            unattempted: unattemptedCount,
            totalQuestions: questions.length,
            timestamp: new Date().toISOString(),
            importantQuestions: importantQuestions,
            language: language
        };

        setScore(correctAnswers);
        setUnattempted(unattemptedCount);
        setShowScore(true);

        if (user) {
            await saveQuizAttempt(user.uid, quizType, quizSet, attemptData);
            alert("Quiz attempt saved successfully!");
        } else {
            alert("Please log in to save your quiz progress.");
        }
    };

    const saveImportantQuestions = async (questionsToSave) => {
        if (!user || !quizType || quizSet === undefined) return;
        const userRef = doc(db, "users", user.uid);
        await setDoc(userRef, {
            quizAttempts: {
                [quizType]: {
                    [quizSet]: {
                        importantQuestions: questionsToSave
                    }
                }
            }
        }, { merge: true });
    };

    const [updatedCategory, setUpdatedCategory] = useState(selectedCategory);

    useEffect(() => {
        const fetchUserQuizAttempts = async () => {
            if (user) {
                const userDocRef = doc(db, "users", user.uid);
                const userDoc = await getDoc(userDocRef);

                if (userDoc.exists()) {
                    const quizAttempts = userDoc.data().quizAttempts || {};

                    if (quizSet !== undefined && quizAttempts[quizType]?.[quizSet]) {
                        setImportantQuestions(quizAttempts[quizType][quizSet].importantQuestions || []);
                    }

                    const updatedSets = selectedCategory?.sets.map(set => ({
                        ...set,
                        attempted: quizAttempts[quizType]?.[set.set] !== undefined,
                        lastScore: quizAttempts[quizType]?.[set.set]?.score
                    }));

                    setUpdatedCategory({
                        ...selectedCategory,
                        sets: updatedSets,
                    });
                }
            }
        };

        fetchUserQuizAttempts();
    }, [user, quizType, quizSet, selectedCategory, location.pathname]);

    // ************* MODIFIED *************
    const getOptionStyle = useCallback((questionId, shuffledIndex) => {
        if (!selectedSet) return {};

        const question = selectedSet.quizzes.find(q => q.id === questionId);
        const correctAnswerEnglish = question.correctAnswer;
        const userSelectedOptionEnglish = selectedOptions[questionId];

        // Get the English text for the *currently displayed option* based on its shuffled index
        const displayedOptionEnglishText = getEnglishTextForShuffledOption(questionId, shuffledIndex);

        // Determine if this displayed option is the one the user selected
        const isSelected = userSelectedOptionEnglish === displayedOptionEnglishText;

        // Determine if this displayed option is the correct answer
        const isCorrectOption = displayedOptionEnglishText === correctAnswerEnglish;

        if (userSelectedOptionEnglish === undefined) {
            // Option not yet selected - modern default style
            return {
                backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                border: darkMode ? "1px solid #444" : "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                transition: "all 0.2s ease"
            };
        }

        if (isSelected) {
            if (isCorrectOption) {
                return {
                    backgroundColor: darkMode ? "#1e4a2a" : "#d4edda",
                    border: darkMode ? "1px solid #2d6a4f" : "1px solid #28a745",
                    color: darkMode ? "#ffffff" : "#155724",
                    boxShadow: darkMode ? "0 4px 8px rgba(0,255,0,0.1)" : "0 4px 8px rgba(40,167,69,0.2)",
                    fontWeight: "600",
                    transition: "all 0.2s ease"
                };
            } else {
                return {
                    backgroundColor: darkMode ? "#5a2a2a" : "#f8d7da",
                    border: darkMode ? "1px solid #b02a37" : "1px solid #dc3545",
                    color: darkMode ? "#ffffff" : "#721c24",
                    boxShadow: darkMode ? "0 4px 8px rgba(255,0,0,0.1)" : "0 4px 8px rgba(220,53,69,0.2)",
                    fontWeight: "600",
                    transition: "all 0.2s ease"
                };
            }
        } else if (isCorrectOption) {
            // If an option has been selected, and this is the correct option (but not the one selected by user)
            return {
                backgroundColor: darkMode ? "#1e4a2a" : "#d4edda",
                border: darkMode ? "1px solid #2d6a4f" : "1px solid #28a745",
                color: darkMode ? "#ffffff" : "#155724",
                opacity: "0.9",
                boxShadow: darkMode ? "0 2px 4px rgba(0,255,0,0.05)" : "0 2px 4px rgba(40,167,69,0.1)",
                transition: "all 0.2s ease"
            };
        }

        return {
            backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
            border: darkMode ? "1px solid #444" : "1px solid #e0e0e0",
            transition: "all 0.2s ease"
        };
    }, [selectedSet, selectedOptions, darkMode, getEnglishTextForShuffledOption]);

    const handleRefresh = () => {
        navigate(`/quizCategory/${quizType}`);
        setSelectedOptions({});
        setShowScore(false);
        setUnattempted(0);
    };

    const recommendedSearchesMap = {
        "current-affairs-sscvidya": ["All", "September"],
        "static-gk": ["All", "MTS 2024", "Census", "Folk Dances", "Olympic", "Books", "Sports", "Gharana", "Songs", "Dance", "Fairs", "Festivals", "Institutes", "Schemes"],
        "history": ["All", "CGL 2024", "MTS 2024", "Mordern", "SSCVIDYA"],
        "polity": ["All", "MTS 2024", "CGL 2024"],
        "geography": ["All", "CGL 2024", "MTS 2024", "sscvidya", "Origin of Universe", "Solar System", "Earth", "Tectonic Plates", "Indian Mountains", "Rocks", "Atmosphere", "Seasons in India", "Borders of India", "Physiographic Divisions", "Himalayan", "Ports in India", "Rivers of India", "Waterfalls", "Waterways"],
        "current-affairs-pyq": ["All", "MTS 2024"],
        "english": ["All", "VOCAB", "IDIOMS"],
        "explore-more": ["All", "SSC 2025", "MTS 2024", "Delhi Police 2023"]
    };

    const [searchQuery, setSearchQuery] = useState('');
    const [showRecommendations, setShowRecommendations] = useState(false);

    const categoryFromUrl = location.pathname.split("/")[2];
    const recommendedSearches = recommendedSearchesMap[categoryFromUrl] || ["All"];

    const filteredSets = useMemo(() => {
        return selectedCategory?.sets.filter(set => {
            if (activeFilter === "All") return true;
            return set.title.toLowerCase().includes(activeFilter.toLowerCase());
        })
            .filter(set => {
                if (searchQuery.trim() === "") return true;
                return set.title.toLowerCase().includes(searchQuery.toLowerCase());
            }) || [];
    }, [selectedCategory, activeFilter, searchQuery]);

    const categoryFromUrl2 = location.pathname.split("/")[3];

    useEffect(() => {
        setSearchQuery("");
        setShowOnly("All");
        setImportantQuestions([]);
        setShowScore(false);
        setTagSearchQuery("");
        setSelectedOptions({}); // Clear selected options when category changes
        setLanguage('en');
        setSelectedTag("All")
    }, [categoryFromUrl2, categoryFromUrl]);

    useEffect(() => {
        setActiveFilter("All");
    }, [categoryFromUrl]);

    const baseQuestions = useMemo(() => {
        return selectedSet ? selectedSet.quizzes : [];
    }, [selectedSet]);

    // Use memoized questions based on filters, then localize them
    const questionsToShowRaw = useMemo(() => {
        let filtered = baseQuestions;

        if (showOnly === "Important") {
            filtered = filtered.filter(q => importantQuestions.includes(q.id));
        }

        if (selectedTag !== "All") {
            filtered = filtered.filter(q =>
                q.tags && q.tags.includes(selectedTag)
            );
        }

        return filtered;
    }, [baseQuestions, showOnly, importantQuestions, selectedTag]);

    // Tags are derived from `baseQuestions` (English only)
    // Fix 1: Separate the tag initialization from URL reading
    useEffect(() => {
        if (baseQuestions.length > 0) {
            const tags = new Set();
            baseQuestions.forEach(q => {
                if (q.tags && Array.isArray(q.tags)) {
                    q.tags.forEach(tag => tags.add(tag));
                }
            });
            const newTags = ["All", ...Array.from(tags).sort()];
            setAvailableTags(newTags);

            // Read tag from URL after setting tags
            const params = new URLSearchParams(location.search);
            const tagFromUrl = params.get('tag');
            if (tagFromUrl && newTags.includes(tagFromUrl)) {
                setSelectedTag(tagFromUrl);
            }
        }
    }, [baseQuestions, location.search]); // Remove availableTags from dependencies

    const filteredTags = useMemo(() => {
        if (!availableTags.length) return [];
        if (tagSearchQuery.trim() === '') return availableTags;
        return availableTags.filter(tag =>
            tag.toLowerCase().includes(tagSearchQuery.toLowerCase())
        );
    }, [availableTags, tagSearchQuery]);

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            @media (max-width: 768px) {
               .hide-on-mobile {
                    display: none!important;
                }
               .show-on-mobile {
                    display: block!important;
                }
                .question-container {
                    padding: 12px !important;
                }
                .question-text {
                    font-size: 16px !important;
                    line-height: 1.5 !important;
                }
                .option-item {
                    padding: 12px 16px !important;
                    font-size: 14px !important;
                    margin: 8px 0 !important;
                }
                .action-button {
                    padding: 8px 12px !important;
                    font-size: 12px !important;
                }
            }
            @media (min-width: 769px) {
               .show-on-mobile {
                    display: none!important;
                }
                .question-container {
                    padding: 24px !important;
                }
                .question-text {
                    font-size: 18px !important;
                }
                .option-item {
                    padding: 14px 20px !important;
                    font-size: 16px !important;
                }
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const handleFocusMode = (question) => {
        // Find the Hindi version of the question
        const hindiQuestion = selectedSet?.quizzesHin?.find(q => q.id === question.id);

        // Create a combined question object with both languages
        const questionWithBothLanguages = {
            ...question,
            questionHi: hindiQuestion?.question,
            optionsHi: hindiQuestion?.options,
            explanationHi: hindiQuestion?.explanation
        };

        setFocusedQuestion(questionWithBothLanguages);
        setFocusMode(true);
    };

    // Function to handle tag selection and update URL
    const handleTagSelection = (tag) => {
        if (tag === selectedTag) return; // Add this check

        setSelectedTag(tag);
        const currentPath = location.pathname;
        const searchParams = new URLSearchParams(location.search);

        if (tag === "All") {
            searchParams.delete('tag');
        } else {
            searchParams.set('tag', tag);
        }

        const newSearch = searchParams.toString();
        const newPath = newSearch ? `${currentPath}?${newSearch}` : currentPath;

        // Only navigate if the URL actually changed
        if (location.pathname + location.search !== newPath) {
            navigate(newPath, { replace: true });
        }
    };

    return (
        <div style={{
            padding: window.innerWidth <= 768 ? '12px' : '20px',
            minHeight: "100vh",
            overflowY: 'auto',
            backgroundColor: darkMode ? '#1a1a1a' : '#f8f9fa'
        }}>
            {selectedCategory && (
                <QuizCategorySEO
                    category={selectedCategory?.category}
                    imageURL={selectedCategory?.imageURL}
                    quizzes={selectedCategory?.sets.flatMap((set) => set.quizzes)}
                />
            )}

            {showHeading && (
                <div className="t-heading" style={{
                    marginBottom: "20px",
                    fontSize: window.innerWidth <= 768 ? '20px' : '24px',
                    fontWeight: '600',
                    color: darkMode ? '#ffffff' : '#333'
                }}>
                    <span>SSC CGL Exam </span>
                    <span>Practice MCQ Quiz </span>
                    <span>Topic-Wise Questions for </span>
                    <span>CGL, MTS, CHSL</span>
                </div>
            )}

            {showSubHeading && (
                <div className="t-heading" style={{
                    marginBottom: "20px",
                    fontSize: window.innerWidth <= 768 ? '18px' : '22px',
                    fontWeight: '600',
                    color: darkMode ? '#ffffff' : '#333'
                }}>
                    <span>Practice MCQ Quiz </span>
                    <span>{quizType ? `${quizType.replace(/-/g, ' ').toUpperCase()}` : 'Select a Quiz:'}</span>
                    <span> Topic-Wise Questions.</span>
                </div>
            )}

            {!quizType && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(auto-fill, minmax(150px, 1fr))' : 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: window.innerWidth <= 768 ? '12px' : '20px',
                    marginBottom: '20px'
                }}>
                    {allData.map((quiz) => (
                        <div
                            key={quiz.category}
                            onClick={() => navigate(`/quizCategory/${quiz.category}`)}
                            style={{
                                cursor: 'pointer',
                                border: '1px solid #ccc',
                                borderRadius: '12px',
                                textAlign: 'center',
                                padding: window.innerWidth <= 768 ? '8px' : '10px',
                                boxShadow: darkMode ? '0 4px 12px rgba(255,255,255,0.1)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                                backgroundColor: darkMode ? '#2d2d2d' : '#fff',
                                transition: 'transform 0.2s ease',
                                ':hover': {
                                    transform: 'scale(1.02)'
                                }
                            }}
                        >
                            <img
                                src={quiz.imageURL}
                                alt={quiz.category}
                                style={{
                                    width: '100%',
                                    borderRadius: '8px',
                                    height: window.innerWidth <= 768 ? '100px' : 'auto',
                                    objectFit: 'cover'
                                }}
                            />
                            <p style={{
                                fontWeight: 'bold',
                                marginTop: '8px',
                                fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                color: darkMode ? '#ffffff' : '#333'
                            }}>
                                {quiz.category.replace(/-/g, ' ').toUpperCase()}
                            </p>
                        </div>
                    ))}
                </div>
            )}

            {quizType && quizSet === undefined && selectedCategory && (
                <div>
                    <div style={{
                        ...styles.filterContainer,
                        justifyContent: window.innerWidth <= 768 ? 'flex-start' : 'center',
                        padding: window.innerWidth <= 768 ? '0 4px' : 0
                    }}>
                        {recommendedSearches.map((filter) => (
                            <button
                                key={filter}
                                onClick={() => setActiveFilter(filter)}
                                style={{
                                    ...styles.filterButton,
                                    ...(activeFilter === filter ? styles.activeFilter : {}),
                                    fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                                    padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px'
                                }}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                    {availableTags.length > 1 && (
                        <div style={styles.searchContainer}>
                            <input
                                type="text"
                                placeholder="Search quiz sets..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    if (e.target.value.trim() === '') {
                                        setShowRecommendations(true);
                                    } else {
                                        setShowRecommendations(false);
                                    }
                                }}
                                onFocus={() => setShowRecommendations(true)}
                                onBlur={() => setTimeout(() => setShowRecommendations(false), 200)}
                                style={{
                                    ...styles.searchInput,
                                    width: window.innerWidth <= 768 ? '95%' : '80%',
                                    fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                    padding: window.innerWidth <= 768 ? '10px' : '12px'
                                }}
                            />
                            {showRecommendations && recommendedSearches.length > 0 && (
                                <div style={{
                                    ...styles.recommendationsContainer,
                                    width: window.innerWidth <= 768 ? '95%' : '80%'
                                }}>
                                    {recommendedSearches.map((term, index) => (
                                        <div
                                            key={index}
                                            onClick={() => setSearchQuery(term)}
                                            style={styles.recommendationItem}
                                        >
                                            {term}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {quizType && quizSet === undefined && selectedCategory && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(auto-fill, minmax(150px, 1fr))' : 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: window.innerWidth <= 768 ? '12px' : '20px',
                    marginBottom: '20px'
                }}>
                    <QuizCategorySEO
                        category={selectedCategory?.category}
                        imageURL={selectedCategory?.imageURL}
                        quizzes={selectedCategory?.sets.flatMap((set) => set.quizzes)}
                    />
                    {filteredSets.length === 0 ? (
                        <p style={{
                            fontSize: window.innerWidth <= 768 ? "16px" : "18px",
                            fontWeight: "bold",
                            color: "#dc3545",
                            textAlign: "center",
                            width: "100%",
                            marginTop: "20px",
                            gridColumn: "1 / -1"
                        }}>
                            ❌ No results found for "{activeFilter}"
                        </p>
                    ) : (filteredSets.map((set) => {
                        const isPremium = set.subscriptionRequired;
                        return (
                            <div
                                key={set.set}
                                onClick={() => {
                                    if (set.authRequired && !user) {
                                        navigate('/login');
                                        return;
                                    }
                                    if (set.subscriptionRequired && !isSubscriptionActive) {
                                        if (window.confirm("🔒 This premium quiz requires subscription. Subscribe now to access?")) {
                                            navigate('/subscription');
                                        }
                                        return;
                                    }
                                    navigate(`/quizCategory/${quizType}/${set.set}`);
                                }}
                                style={{
                                    cursor: (isPremium && !isSubscriptionActive) ? 'not-allowed' : 'pointer',
                                    border: isPremium ? '2px solid gold' : '1px solid #ccc',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    padding: window.innerWidth <= 768 ? '12px' : '15px',
                                    boxShadow: darkMode ? '0 4px 12px rgba(255,255,255,0.1)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                                    position: 'relative',
                                    backgroundColor: darkMode ? '#2d2d2d' : '#fff',
                                    opacity: isPremium && !isSubscriptionActive ? 0.7 : 1,
                                    transition: 'transform 0.2s ease',
                                    ':hover': {
                                        transform: 'scale(1.02)'
                                    }
                                }}
                            >
                                {isPremium && (
                                    <span style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: 'gold',
                                        color: 'black',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: window.innerWidth <= 768 ? '9px' : '10px',
                                        fontWeight: 'bold'
                                    }}>
                                        PREMIUM
                                    </span>
                                )}
                                <p style={{
                                    fontWeight: 'bold',
                                    marginTop: '10px',
                                    fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                    color: darkMode ? '#ffffff' : (isPremium && !isSubscriptionActive ? '#6c757d' : '#000')
                                }}>
                                    {set.title} <br />
                                    <span style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px', color: '#666' }}>
                                        (Q: {set.quizzes.length})
                                    </span>
                                </p>
                                {set.authRequired && !user && (
                                    <p style={{
                                        color: '#dc3545',
                                        fontSize: window.innerWidth <= 768 ? '11px' : '12px',
                                        marginTop: '5px'
                                    }}>
                                        Please login to access.
                                    </p>
                                )}
                                {isPremium && !isSubscriptionActive && (
                                    <div style={{ marginTop: '5px' }}>
                                        <span style={{
                                            color: '#dc3545',
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px'
                                        }}>
                                            🔒 Subscription Required
                                        </span>
                                    </div>
                                )}
                                {user && updatedCategory?.sets?.find(s => s.set === set.set)?.attempted && (
                                    <p style={{ marginTop: '8px' }}>
                                        <span style={{
                                            color: "#28a745",
                                            fontSize: window.innerWidth <= 768 ? "12px" : "14px",
                                            fontWeight: "500"
                                        }}>
                                            ✓ Completed! Score: {updatedCategory?.sets?.find(s => s.set === set.set)?.lastScore}
                                        </span>
                                    </p>
                                )}
                            </div>
                        );
                    }))}
                </div>
            )}

            {selectedSet && (
                <div>
                    {/* Filter buttons - All, Important, and Language options */}
                    <div style={{
                        ...styles.filterContainer,
                        justifyContent: window.innerWidth <= 768 ? 'flex-start' : 'center',
                        padding: window.innerWidth <= 768 ? '0 4px' : 0,
                        marginBottom: window.innerWidth <= 768 ? '12px' : '20px'
                    }}>
                        <button
                            onClick={() => setShowOnly("All")}
                            style={{
                                padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                                borderRadius: '25px',
                                border: 'none',
                                background: showOnly === "All" ? '#007bff' : darkMode ? '#444' : '#f0f0f0',
                                color: showOnly === "All" ? 'white' : darkMode ? '#fff' : '#333',
                                cursor: 'pointer',
                                fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                                fontWeight: '500',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            All
                        </button>
                        {/* IMPORTANT BUTTON */}
                        <button
                            onClick={() => {
                                if (!user) {
                                    alert("Please login to see marked questions!");
                                    return;
                                }
                                if (importantQuestions.length === 0) {
                                    alert("No marked questions found. Please mark questions (★) to see them here.");
                                    return;
                                }
                                setShowOnly("Important")
                            }}
                            style={{
                                padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                                borderRadius: '25px',
                                border: 'none',
                                background: showOnly === "Important" ? '#007bff' : darkMode ? '#444' : '#f0f0f0',
                                color: showOnly === "Important" ? 'white' : darkMode ? '#fff' : '#333',
                                cursor: 'pointer',
                                fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                                fontWeight: '500',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            Important ({importantQuestions.length})
                        </button>

                        {/* Language Selection Buttons */}
                        {selectedSet.quizzesHin && (
                            <>
                                <button
                                    onClick={() => setLanguage('en')}
                                    style={{
                                        padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                                        borderRadius: '25px',
                                        border: 'none',
                                        background: language === 'en' ? '#28a745' : darkMode ? '#444' : '#f0f0f0',
                                        color: language === 'en' ? 'white' : darkMode ? '#fff' : '#333',
                                        cursor: 'pointer',
                                        fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                                        fontWeight: '500',
                                        marginLeft: window.innerWidth <= 768 ? '5px' : '10px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    English
                                </button>
                                <button
                                    onClick={() => setLanguage('hi')}
                                    style={{
                                        padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                                        borderRadius: '25px',
                                        border: 'none',
                                        background: language === 'hi' ? '#28a745' : darkMode ? '#444' : '#f0f0f0',
                                        color: language === 'hi' ? 'white' : darkMode ? '#fff' : '#333',
                                        cursor: 'pointer',
                                        fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                                        fontWeight: '500',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    Hindi
                                </button>
                                <button
                                    onClick={() => setLanguage('bilingual')}
                                    style={{
                                        padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                                        borderRadius: '25px',
                                        border: 'none',
                                        background: language === 'bilingual' ? '#28a745' : darkMode ? '#444' : '#f0f0f0',
                                        color: language === 'bilingual' ? 'white' : darkMode ? '#fff' : '#333',
                                        cursor: 'pointer',
                                        fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                                        fontWeight: '500',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    Bilingual
                                </button>
                            </>
                        )}
                    </div>

                    {/* Tag filter chips */}
                    {availableTags.length > 1 && (
                        <div style={{
                            ...styles.filterContainer,
                            marginTop: '10px',
                            marginBottom: window.innerWidth <= 768 ? '15px' : '20px',
                            justifyContent: window.innerWidth <= 768 ? 'flex-start' : 'center',
                            gap: window.innerWidth <= 768 ? '6px' : '8px'
                        }} className="hide-on-mobile">
                            {availableTags.map((tag) => (
                                <button
                                    key={tag}
                                    // onClick={() => setSelectedTag(tag)}
                                    onClick={() => handleTagSelection(tag)} // Use the new handler here

                                    style={{
                                        padding: window.innerWidth <= 768 ? '4px 10px' : '6px 14px',
                                        borderRadius: '20px',
                                        border: 'none',
                                        background: selectedTag === tag ? '#28a745' : darkMode ? '#444' : '#f0f0f0',
                                        color: selectedTag === tag ? 'white' : darkMode ? '#fff' : '#333',
                                        cursor: 'pointer',
                                        fontSize: window.innerWidth <= 768 ? '11px' : '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {tag} {tag !== "All" && `(${baseQuestions.filter(q => q.tags?.includes(tag)).length})`}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Tag Search Container for Mobile */}
                    {availableTags.length > 1 && (
                        <div style={styles.searchContainer} className="show-on-mobile">
                            <div style={{ position: 'relative', width: '100%' }}>

                                <input
                                    type="text"
                                    placeholder="Search topics/tags..."
                                    value={tagSearchQuery}
                                    onChange={(e) => {
                                        setTagSearchQuery(e.target.value);
                                        setShowTagRecommendations(true);  // Always true when typing
                                    }}
                                    onFocus={() => setShowTagRecommendations(true)}
                                    onBlur={() => setTimeout(() => setShowTagRecommendations(false), 200)}
                                    style={{
                                        ...styles.searchInput,
                                        width: '95%',
                                        fontSize: '14px',
                                        padding: '10px'
                                    }}
                                />
                                {tagSearchQuery && (
                                    <button
                                        onClick={() => {
                                            setTagSearchQuery('');
                                            // setSelectedTag('All');
                                            handleTagSelection('All'); // <-- Change this line to use handleTagSelection
                                            setShowTagRecommendations(false);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '18px',
                                            cursor: 'pointer',
                                            color: '#666',
                                            padding: '5px 10px'
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            {showTagRecommendations && filteredTags.length > 0 && (
                                <div style={{
                                    ...styles.recommendationsContainer,
                                    width: '100%',
                                    maxHeight: '350px'
                                }}>
                                    {filteredTags.map((tag, index) => (
                                        <div
                                            key={index}
                                            onClick={() => {
                                                // setSelectedTag(tag);
                                                handleTagSelection(tag); // Use the new handler here
                                                setTagSearchQuery(tag);
                                                setShowTagRecommendations(false);
                                            }}
                                            style={{
                                                ...styles.recommendationItem,
                                                ...(selectedTag === tag ? { backgroundColor: '#007bff', color: 'white' } : {}),
                                                fontSize: '13px',
                                                padding: '10px'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#007bff';
                                                e.target.style.color = 'white';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedTag !== tag) {
                                                    e.target.style.backgroundColor = '#fff';
                                                    e.target.style.color = '#333';
                                                }
                                            }}
                                        >
                                            {tag} {tag !== "All" && `(${baseQuestions.filter(q => q.tags?.includes(tag)).length})`}
                                            {selectedTag === tag && ' ✓'}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Questions Display */}
                    {questionsToShowRaw.map((question, index) => {
                        const displayQuestion = getLocalizedQuestion(question);

                        return (
                            <div
                                key={question.id}
                                id={`question-${question.id}`}
                                className="question-container"
                                style={{
                                    marginBottom: window.innerWidth <= 768 ? '20px' : '30px',
                                    backgroundColor: darkMode ? '#2d2d2d' : '#ffffff',
                                    borderRadius: '16px',
                                    padding: window.innerWidth <= 768 ? '16px' : '24px',
                                    boxShadow: darkMode ? '0 4px 12px rgba(255,255,255,0.05)' : '0 4px 12px rgba(0, 0, 0, 0.08)'
                                }}
                            >
                                <span style={{
                                    whiteSpace: "pre-line",
                                    color: darkMode ? '#ffffff' : '#333',
                                    marginBottom: '16px',
                                    lineHeight: '1.4',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start'
                                }}>
                                    <span>
                                        <span style={{
                                            color: '#007bff', fontWeight: '600', marginRight: '8px', fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                        }}>{index + 1}.</span>
                                        <span style={{ fontWeight: '600', fontSize: window.innerWidth <= 768 ? '14px' : '16px', }} >{displayQuestion.question}</span>
                                    </span>
                                    {/* <button
                                        onClick={async () => {
                                            if (!user) {
                                                alert("Please login to mark questions!");
                                                return;
                                            }
                                            const updatedImportantQuestions = importantQuestions.includes(question.id)
                                                ? importantQuestions.filter(id => id !== question.id)
                                                : [...importantQuestions, question.id];
                                            setImportantQuestions(updatedImportantQuestions);
                                            await saveImportantQuestions(updatedImportantQuestions);
                                            if (updatedImportantQuestions.length === 0) {
                                                setShowOnly("All");
                                            }
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: window.innerWidth <= 768 ? '20px' : '24px',
                                            cursor: 'pointer',
                                            color: importantQuestions.includes(question.id) ? '#ffc107' : darkMode ? '#666' : '#ccc',
                                            marginLeft: '10px',
                                            flexShrink: 0
                                        }}
                                    >
                                        {importantQuestions.includes(question.id) ? '★' : '☆'}
                                    </button> */}
                                </span>
                                {question?.exam && (
                                    <p style={{
                                        fontSize: window.innerWidth <= 768 ? '11px' : '12px',
                                        color: '#666',
                                        marginBottom: '12px',
                                        fontStyle: 'italic'
                                    }}>
                                        {question.exam}
                                    </p>
                                )}
                                <div style={{ marginTop: '16px' }}>
                                    {displayQuestion.options.map((option, shuffledIndex) => {
                                        const isSelectedForDisplay = selectedOptions[question.id] !== undefined &&
                                            getEnglishTextForShuffledOption(question.id, shuffledIndex) === selectedOptions[question.id];

                                        return (
                                            <div
                                                key={option}
                                                onClick={() => handleOptionClick(question.id, shuffledIndex)}
                                                className="option-item"
                                                style={{
                                                    padding: window.innerWidth <= 768 ? '12px 16px' : '14px 20px',
                                                    margin: '8px 0',
                                                    cursor: selectedOptions[question.id] !== undefined ? 'default' : 'pointer',
                                                    ...getOptionStyle(question.id, shuffledIndex),
                                                    fontWeight: isSelectedForDisplay ? '600' : '400',
                                                    borderRadius: '12px',
                                                    fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                                    lineHeight: '1.5',
                                                    opacity: selectedOptions[question.id] !== undefined && !isSelectedForDisplay ? '0.9' : '1'
                                                }}
                                            >
                                                {option}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    marginTop: window.innerWidth <= 768 ? '16px' : '20px',
                                    gap: '8px',
                                    flexWrap: 'wrap'
                                }}>
                                    {/* DOWNLOAD AS IMAGE */}
                                    <button
                                        onClick={() => {
                                            if (!user) {
                                                alert("Please login to download the image!");
                                                return;
                                            }
                                            handleShareClick(question)
                                        }}
                                        className="action-button"
                                        style={{
                                            padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            backgroundColor: '#007bff',
                                            color: 'white',
                                            fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        Image <FaDownload size={window.innerWidth <= 768 ? 10 : 12} />
                                    </button>
                                    {/* DOWNLOAD AS VIDEO */}
                                    <button
                                        onClick={() => {
                                            if (!user) {
                                                alert("Please login to download the video!");
                                                return;
                                            }
                                            handleVideoShareClick(question)
                                        }}
                                        className="action-button"
                                        style={{
                                            padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        Video <FaDownload size={window.innerWidth <= 768 ? 10 : 12} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (!user) {
                                                alert("Please login to mark questions!");
                                                return;
                                            }
                                            const updatedImportantQuestions = importantQuestions.includes(question.id)
                                                ? importantQuestions.filter(id => id !== question.id)
                                                : [...importantQuestions, question.id];
                                            setImportantQuestions(updatedImportantQuestions);
                                            saveImportantQuestions(updatedImportantQuestions);
                                            if (updatedImportantQuestions.length === 0) {
                                                setShowOnly("All");
                                            }
                                        }}
                                        className="action-button"
                                        style={{
                                            padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            backgroundColor: importantQuestions.includes(question.id) ? '#ffc107' : '#6c757d',
                                            color: importantQuestions.includes(question.id) ? '#000' : 'white',
                                            fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontWeight: importantQuestions.includes(question.id) ? '600' : '400',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        {importantQuestions.includes(question.id) ? '★ Important' : '☆ Mark Important'}
                                    </button>
                                    {/* Focus Mode Button - Add this first */}
                                    {user && FOCUS_MODE_AUTHORIZED_EMAILS.includes(user.email) && (

                                        <FocusModeButton
                                            onClick={() => handleFocusMode(question)}
                                            darkMode={darkMode}
                                        />)}
                                    {question.explanation && (
                                        <button
                                            onClick={() => {
                                                if (!user) {
                                                    alert("Please login to see the explanation!");
                                                    return;
                                                }
                                                if (selectedOptions[question.id] !== undefined) {
                                                    handleExplanationClick(question);
                                                } else {
                                                    alert("Please select an option to see the explanation!");
                                                }
                                            }}
                                            className="action-button"
                                            style={{
                                                padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                backgroundColor: '#fd7e14',
                                                color: 'white',
                                                fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                                                cursor: selectedOptions[question.id] !== undefined ? "pointer" : "not-allowed",
                                                opacity: selectedOptions[question.id] !== undefined ? 1 : 0.5,
                                                transition: 'background-color 0.2s'
                                            }}
                                        >
                                            Explain
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {showOnly === "All" && selectedTag === "All" && (
                        <button
                            onClick={calculateScore}
                            style={{
                                padding: window.innerWidth <= 768 ? '12px 24px' : '14px 32px',
                                fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                                fontWeight: '600',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '30px',
                                cursor: 'pointer',
                                width: window.innerWidth <= 768 ? '100%' : 'auto',
                                marginTop: '20px',
                                boxShadow: '0 4px 12px rgba(0,123,255,0.3)',
                                transition: 'transform 0.2s'
                            }}
                        >
                            Submit Quiz
                        </button>
                    )}

                    {showScore && showOnly === "All" && (
                        <div style={{
                            marginTop: '30px',
                            padding: window.innerWidth <= 768 ? '20px' : '24px',
                            backgroundColor: darkMode ? '#2d2d2d' : '#ffffff',
                            borderRadius: '16px',
                            boxShadow: darkMode ? '0 4px 12px rgba(255,255,255,0.05)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
                            textAlign: 'center'
                        }}>
                            <h2 style={{
                                fontSize: window.innerWidth <= 768 ? '20px' : '24px',
                                color: darkMode ? '#28a745' : '#28a745',
                                marginBottom: '12px'
                            }}>
                                Your Score: {score} / {selectedSet.quizzes.length}
                            </h2>
                            <h2 style={{
                                fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                                color: darkMode ? '#dc3545' : '#dc3545',
                                marginBottom: '20px'
                            }}>
                                Unattempted: {unattempted}
                            </h2>
                            <button
                                onClick={handleRefresh}
                                style={{
                                    padding: window.innerWidth <= 768 ? '10px 20px' : '12px 24px',
                                    fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '30px',
                                    cursor: 'pointer',
                                    width: window.innerWidth <= 768 ? '100%' : 'auto'
                                }}
                            >
                                Back to Quiz Sets
                            </button>
                        </div>
                    )}

                    {showPopup && activeQuestion && (
                        <SharePopup
                            question={activeQuestion}
                            onClose={() => setShowPopup(false)}
                            language={language} // Pass the current language from parent
                        />
                    )}

                    {showVideoPopup && activeVideoQuestion && (
                        <SharePopupWithVideo
                            question={activeVideoQuestion}
                            onClose={() => setShowVideoPopup(false)}
                        />
                    )}

                    {showExplanationPopup && activeExplanationQuestion && (
                        <ExplanationPopup
                            question={activeExplanationQuestion}
                            onClose={() => setShowExplanationPopup(false)}
                            language={language} // Pass the current language from parent
                        />
                    )}

                    {/* Focus Mode Popup */}
                    {/* <FocusModePopup
                        isOpen={focusMode}
                        onClose={() => {
                            setFocusMode(false);
                            setFocusedQuestion(null);
                        }}
                        question={focusedQuestion}
                        language={language}
                        questionNumber={focusedQuestion ? questionsToShowRaw.findIndex(q => q.id === focusedQuestion.id) + 1 : 0}
                    /> */}
                    {/* Focus Mode Popup */}
                    <FocusModePopup
                        isOpen={focusMode}
                        onClose={() => {
                            setFocusMode(false);
                            setFocusedQuestion(null);
                        }}
                        question={focusedQuestion}
                        language={language}
                        questionNumber={focusedQuestion ? questionsToShowRaw.findIndex(q => q.id === focusedQuestion.id) + 1 : 0}
                        totalQuestions={questionsToShowRaw.length}
                        showCorrectAnswer={true}  // Add this line
                        selectedTag={selectedTag} // Add this line
                        onNextQuestion={() => {
                            const currentIndex = questionsToShowRaw.findIndex(q => q.id === focusedQuestion.id);
                            if (currentIndex < questionsToShowRaw.length - 1) {
                                const nextQuestion = questionsToShowRaw[currentIndex + 1];
                                const hindiQuestion = selectedSet?.quizzesHin?.find(q => q.id === nextQuestion.id);
                                setFocusedQuestion({
                                    ...nextQuestion,
                                    questionHi: hindiQuestion?.question,
                                    optionsHi: hindiQuestion?.options,
                                    explanationHi: hindiQuestion?.explanation
                                });
                            }
                        }}
                        onPrevQuestion={() => {
                            const currentIndex = questionsToShowRaw.findIndex(q => q.id === focusedQuestion.id);
                            if (currentIndex > 0) {
                                const prevQuestion = questionsToShowRaw[currentIndex - 1];
                                const hindiQuestion = selectedSet?.quizzesHin?.find(q => q.id === prevQuestion.id);
                                setFocusedQuestion({
                                    ...prevQuestion,
                                    questionHi: hindiQuestion?.question,
                                    optionsHi: hindiQuestion?.options,
                                    explanationHi: hindiQuestion?.explanation
                                });
                            }
                        }}
                    />

                </div>
            )}
        </div>
    );
}

const styles = {
    filterContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '20px',
        alignItems: 'center'
    },
    filterButton: {
        padding: '8px 16px',
        borderRadius: '25px',
        border: 'none',
        background: '#f0f0f0',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    activeFilter: {
        background: '#007bff',
        color: 'white'
    },
    searchContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        marginBottom: "20px",
        width: "100%",
    },
    searchInput: {
        padding: "12px",
        width: "80%",
        borderRadius: "30px",
        border: "2px solid #007bff",
        fontSize: "16px",
        outline: "none",
        transition: "0.3s",
        boxShadow: "0 2px 8px rgba(0, 123, 255, 0.2)"
    },
    recommendationsContainer: {
        position: "absolute",
        width: "100%",
        background: "#fff",
        border: "1px solid #ddd",
        borderRadius: "12px",
        boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
        zIndex: 10,
        overflowY: "auto",
        maxHeight: "50%",
    },
    recommendationItem: {
        padding: "12px",
        cursor: "pointer",
        fontSize: "14px",
        borderBottom: "1px solid #f0f0f0",
        transition: "0.3s",
        textAlign: "left",
        color: "#333",
        backgroundColor: "#fff",
        ':hover': {
            backgroundColor: '#007bff',
            color: 'white'
        }
    },
    clearSearchButton: {
        position: "absolute",
        right: "12px",
        background: "none",
        border: "none",
        fontSize: "18px",
        cursor: "pointer",
        color: "#666",
        padding: "4px 8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        transition: "all 0.2s ease",
        ':hover': {
            backgroundColor: "#f0f0f0",
            color: "#333"
        }
    }
};

export default DummyPage;

//Older Version 2
// import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
// import { useParams, useNavigate, useLocation } from 'react-router-dom';
// import allData from "../data/allData";
// import { themeContext } from '../Context';
// import { auth } from '../firebase/firebaseConfig';
// import QuizCategorySEO from '../utilities/SEO/QuizCategorySEO';
// import SharePopup from '../utilities/SharePopup';
// import SharePopupWithVideo from '../utilities/SharePopupWithVideo';
// import ExplanationPopup from '../utilities/ExplanationPopup';
// import { saveQuizAttempt } from '../utilities/SaveQuizAttempt';
// import { doc, getDoc, setDoc } from "firebase/firestore";
// import { db } from '../firebase/firebaseConfig';
// import { FaDownload } from 'react-icons/fa';

// //SUBS
// import { useSubscription } from '../utilities/SubscriptionContext';

// function DummyPage() {
//     const { quizType, quizSet } = useParams();
//     const navigate = useNavigate();
//     const location = useLocation();
//     const [selectedOptions, setSelectedOptions] = useState({});
//     const [showScore, setShowScore] = useState(false);
//     const [score, setScore] = useState(0);
//     const [unattempted, setUnattempted] = useState(0);
//     const [user, setUser] = useState(null);
//     const [authLoading, setAuthLoading] = useState(true);

//     // Language State
//     const [language, setLanguage] = useState('en'); // 'en', 'hi', 'bilingual'

//     // Store shuffled indices to maintain consistency across languages
//     const [shuffledIndices, setShuffledIndices] = useState({});

//     // State to Manage Popup Visibility
//     const [showPopup, setShowPopup] = useState(false);
//     const [activeQuestion, setActiveQuestion] = useState(null);
//     const [activeVideoQuestion, setActiveVideoQuestion] = useState(null);
//     const [showVideoPopup, setShowVideoPopup] = useState(false);

//     //MARKING IMPORTANT QUESTIONS
//     const [importantQuestions, setImportantQuestions] = useState([]);
//     const [showOnly, setShowOnly] = useState("All");

//     //Search Chips
//     const [activeFilter, setActiveFilter] = useState("All");

//     //SUBS
//     const { isSubscriptionActive, loading } = useSubscription();

//     //TAGS
//     const [selectedTag, setSelectedTag] = useState("All");
//     const [availableTags, setAvailableTags] = useState([]);
//     const [tagSearchQuery, setTagSearchQuery] = useState('');
//     const [showTagRecommendations, setShowTagRecommendations] = useState(false);

//     // Explanation Declarations
//     const [showExplanationPopup, setShowExplanationPopup] = useState(false);
//     const [activeExplanationQuestion, setActiveExplanationQuestion] = useState(null);

//     const theme = useContext(themeContext);
//     const darkMode = theme.state.darkMode;

//     const selectedCategory = useMemo(() => allData.find(category => category.category === quizType), [quizType]);
//     const selectedSet = useMemo(() => selectedCategory?.sets.find(set => set.set === quizSet) || null, [selectedCategory, quizSet]);

//     // Helper to get the original English text for a given shuffled option index
//     const getEnglishTextForShuffledOption = useCallback((questionId, shuffledIndex) => {
//         const originalQuestion = selectedSet?.quizzes.find(q => q.id === questionId);
//         const originalEnglishOptions = originalQuestion?.options || [];
//         const shuffleOrder = shuffledIndices[questionId] || [];
//         const originalUnshuffledIndex = shuffleOrder[shuffledIndex];
//         return originalEnglishOptions[originalUnshuffledIndex];
//     }, [selectedSet, shuffledIndices]);

//     // Generate consistent shuffled indices for each question
//     useEffect(() => {
//         if (selectedSet) {
//             const indices = {};
//             selectedSet.quizzes.forEach(question => {
//                 // Create a deterministic shuffle based on question id
//                 const seed = question.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
//                 const optionsCount = question.options.length;
//                 const array = Array.from({ length: optionsCount }, (_, i) => i);

//                 // Simple deterministic shuffle
//                 for (let i = array.length - 1; i > 0; i--) {
//                     // Use a slightly more robust deterministic shuffle if needed,
//                     // but the core idea is to get a consistent order for a given seed.
//                     const j = (seed + i) % (i + 1);
//                     [array[i], array[j]] = [array[j], array[i]];
//                 }

//                 indices[question.id] = array;
//             });
//             setShuffledIndices(indices);
//         }
//     }, [selectedSet]);

//     // Helper function to get localized question data with consistent option ordering
//     const getLocalizedQuestion = useCallback((question) => {
//         if (!question) return null;

//         const hinQuestion = selectedSet?.quizzesHin?.find(q => q.id === question.id);
//         const shuffleOrder = shuffledIndices[question.id] || [];

//         // Get original (unshuffled) options arrays
//         const enOptionsOriginal = question.options;
//         const hiOptionsOriginal = hinQuestion?.options || [];

//         // Apply same shuffle order to both language options
//         const shuffledEnOptions = shuffleOrder.map(index => enOptionsOriginal[index]);
//         // If Hindi translation is missing for an option, ensure it doesn't break the pairing
//         const shuffledHiOptions = shuffleOrder.map(index => hiOptionsOriginal[index] || '');

//         if (language === 'en') {
//             return {
//                 ...question,
//                 options: shuffledEnOptions
//             };
//         }

//         if (language === 'hi' && hinQuestion) {
//             return {
//                 ...hinQuestion,
//                 options: shuffledHiOptions
//             };
//         }

//         if (language === 'bilingual' && hinQuestion) {
//             return {
//                 ...question, // Keep original question data as base
//                 question: `${question.question}\n\n${hinQuestion.question}`,
//                 options: shuffledEnOptions.map((enOpt, idx) => {
//                     const hiOpt = shuffledHiOptions[idx];
//                     return hiOpt ? `${enOpt} / ${hiOpt}` : enOpt; // Only add Hindi if available
//                 }),
//                 correctAnswer: question.correctAnswer, // Keep English for answer checking
//                 explanation: question.explanation
//             };
//         }

//         // Fallback to English if Hindi not available or other issues
//         return {
//             ...question,
//             options: shuffledEnOptions
//         };
//     }, [language, selectedSet, shuffledIndices]);

//     // Handler to Open Popup
//     const handleShareClick = (question) => {
//         setActiveQuestion(question);
//         setShowPopup(true);
//     };

//     const handleVideoShareClick = (question) => {
//         setActiveVideoQuestion(question);
//         setShowVideoPopup(true);
//     };

//     const handleExplanationClick = (question) => {
//         if (!user) {
//             alert("Please login to see the explanation!");
//             return;
//         }
//         setActiveExplanationQuestion(question);
//         setShowExplanationPopup(true);
//     };

//     // Effect to track the authentication state
//     useEffect(() => {
//         const unsubscribe = auth.onAuthStateChanged(currentUser => {
//             setUser(currentUser);
//             setAuthLoading(false);
//         });
//         return () => unsubscribe();
//     }, []);

//     // Determine if we should display the header based on the current path
//     const showHeading = location.pathname === `/quizCategory` || location.pathname.endsWith('/quizcategory/');
//     const showSubHeading = location.pathname.startsWith('/quizCategory/') && location.pathname !== '/quizCategory/';

//     // Redirect if quiz set requires authentication and the user is not logged in
//     useEffect(() => {
//         if (!authLoading && selectedSet && selectedSet.authRequired && !user) {
//             navigate('/login');
//         }
//     }, [selectedSet, user, navigate, authLoading]);

//     useEffect(() => {
//         if (loading) return;
//         if (selectedSet && selectedSet.subscriptionRequired && !isSubscriptionActive) {
//             alert("This premium quiz requires subscription. Redirecting to subscription page.");
//             navigate('/subscription');
//         }
//     }, [selectedSet, isSubscriptionActive, navigate, loading]);

//     // ************* MODIFIED *************
//     const handleOptionClick = useCallback((questionId, shuffledIndex) => {
//         if (selectedOptions[questionId] !== undefined) return; // Prevent changing selection once made

//         // Get the English text of the selected option based on its shuffled index
//         const selectedOptionEnglishText = getEnglishTextForShuffledOption(questionId, shuffledIndex);

//         setSelectedOptions(prevOptions => ({
//             ...prevOptions,
//             [questionId]: selectedOptionEnglishText, // Always store the English text from the original question
//         }));
//     }, [selectedOptions, getEnglishTextForShuffledOption]);

//     const calculateScore = async () => {
//         let correctAnswers = 0;
//         let unattemptedCount = 0;

//         const questions = selectedSet ? selectedSet.quizzes : [];
//         questions.forEach((question) => {
//             const userAnswer = selectedOptions[question.id];
//             // The `userAnswer` is already stored as the English text,
//             // so direct comparison with `question.correctAnswer` (which should also be English) is correct.
//             if (userAnswer === question.correctAnswer) {
//                 correctAnswers += 1;
//             } else if (userAnswer === undefined) { // Check for undefined, not just falsy
//                 unattemptedCount += 1;
//             }
//         });

//         const attemptData = {
//             score: correctAnswers,
//             unattempted: unattemptedCount,
//             totalQuestions: questions.length,
//             timestamp: new Date().toISOString(),
//             importantQuestions: importantQuestions,
//             language: language
//         };

//         setScore(correctAnswers);
//         setUnattempted(unattemptedCount);
//         setShowScore(true);

//         if (user) {
//             await saveQuizAttempt(user.uid, quizType, quizSet, attemptData);
//             alert("Quiz attempt saved successfully!");
//         } else {
//             alert("Please log in to save your quiz progress.");
//         }
//     };

//     const saveImportantQuestions = async (questionsToSave) => {
//         if (!user || !quizType || quizSet === undefined) return;
//         const userRef = doc(db, "users", user.uid);
//         await setDoc(userRef, {
//             quizAttempts: {
//                 [quizType]: {
//                     [quizSet]: {
//                         importantQuestions: questionsToSave
//                     }
//                 }
//             }
//         }, { merge: true });
//     };

//     const [updatedCategory, setUpdatedCategory] = useState(selectedCategory);

//     useEffect(() => {
//         const fetchUserQuizAttempts = async () => {
//             if (user) {
//                 const userDocRef = doc(db, "users", user.uid);
//                 const userDoc = await getDoc(userDocRef);

//                 if (userDoc.exists()) {
//                     const quizAttempts = userDoc.data().quizAttempts || {};

//                     if (quizSet !== undefined && quizAttempts[quizType]?.[quizSet]) {
//                         setImportantQuestions(quizAttempts[quizType][quizSet].importantQuestions || []);
//                     }

//                     const updatedSets = selectedCategory?.sets.map(set => ({
//                         ...set,
//                         attempted: quizAttempts[quizType]?.[set.set] !== undefined,
//                         lastScore: quizAttempts[quizType]?.[set.set]?.score
//                     }));

//                     setUpdatedCategory({
//                         ...selectedCategory,
//                         sets: updatedSets,
//                     });
//                 }
//             }
//         };

//         fetchUserQuizAttempts();
//     }, [user, quizType, quizSet, selectedCategory, location.pathname]);

//     // ************* MODIFIED *************
//     const getOptionStyle = useCallback((questionId, shuffledIndex) => {
//         if (!selectedSet) return {};

//         const question = selectedSet.quizzes.find(q => q.id === questionId);
//         const correctAnswerEnglish = question.correctAnswer;
//         const userSelectedOptionEnglish = selectedOptions[questionId];

//         // Get the English text for the *currently displayed option* based on its shuffled index
//         const displayedOptionEnglishText = getEnglishTextForShuffledOption(questionId, shuffledIndex);

//         // Determine if this displayed option is the one the user selected
//         const isSelected = userSelectedOptionEnglish === displayedOptionEnglishText;

//         // Determine if this displayed option is the correct answer
//         const isCorrectOption = displayedOptionEnglishText === correctAnswerEnglish;

//         if (userSelectedOptionEnglish === undefined) {
//             // Option not yet selected, just return default background
//             return { backgroundColor: darkMode ? "rgb(128 128 128 / 42%)" : '#f0f0f0' };
//         }

//         if (isSelected) {
//             if (isCorrectOption) {
//                 return { backgroundColor: 'lightgreen', color: "black" };
//             } else {
//                 return { backgroundColor: 'lightcoral', color: "black" };
//             }
//         } else if (isCorrectOption) {
//             // If an option has been selected, and this is the correct option (but not the one selected by user)
//             return { backgroundColor: 'lightgreen', color: "black" };
//         }

//         return { backgroundColor: darkMode ? "rgb(128 128 128 / 42%)" : '#f0f0f0' };
//     }, [selectedSet, selectedOptions, darkMode, getEnglishTextForShuffledOption]);

//     const handleRefresh = () => {
//         navigate(`/quizCategory/${quizType}`);
//         setSelectedOptions({});
//         setShowScore(false);
//         setUnattempted(0);
//     };

//     const recommendedSearchesMap = {
//         "current-affairs-sscvidya": ["All", "September"],
//         "static-gk": ["All", "MTS 2024", "Census", "Folk Dances", "Olympic", "Books", "Sports", "Gharana", "Songs", "Dance", "Fairs", "Festivals", "Institutes", "Schemes"],
//         "history": ["All", "CGL 2024", "MTS 2024", "Mordern", "SSCVIDYA"],
//         "polity": ["All", "MTS 2024", "CGL 2024"],
//         "geography": ["All", "CGL 2024", "MTS 2024", "sscvidya", "Origin of Universe", "Solar System", "Earth", "Tectonic Plates", "Indian Mountains", "Rocks", "Atmosphere", "Seasons in India", "Borders of India", "Physiographic Divisions", "Himalayan", "Ports in India", "Rivers of India", "Waterfalls", "Waterways"],
//         "current-affairs-pyq": ["All", "MTS 2024"],
//         "english": ["All", "VOCAB", "IDIOMS"],
//         "explore-more": ["All", "SSC 2025", "MTS 2024", "Delhi Police 2023"]
//     };

//     const [searchQuery, setSearchQuery] = useState('');
//     const [showRecommendations, setShowRecommendations] = useState(false);

//     const categoryFromUrl = location.pathname.split("/")[2];
//     const recommendedSearches = recommendedSearchesMap[categoryFromUrl] || ["All"];

//     const filteredSets = useMemo(() => {
//         return selectedCategory?.sets.filter(set => {
//             if (activeFilter === "All") return true;
//             return set.title.toLowerCase().includes(activeFilter.toLowerCase());
//         })
//             .filter(set => {
//                 if (searchQuery.trim() === "") return true;
//                 return set.title.toLowerCase().includes(searchQuery.toLowerCase());
//             }) || [];
//     }, [selectedCategory, activeFilter, searchQuery]);

//     const categoryFromUrl2 = location.pathname.split("/")[3];

//     useEffect(() => {
//         setSearchQuery("");
//         setShowOnly("All");
//         setImportantQuestions([]);
//         setShowScore(false);
//         setTagSearchQuery("");
//         setSelectedOptions({}); // Clear selected options when category changes
//         setLanguage('en');
//         setSelectedTag("All")
//     }, [categoryFromUrl2, categoryFromUrl]);

//     useEffect(() => {
//         setActiveFilter("All");
//     }, [categoryFromUrl]);

//     const baseQuestions = useMemo(() => {
//         return selectedSet ? selectedSet.quizzes : [];
//     }, [selectedSet]);

//     // Use memoized questions based on filters, then localize them
//     const questionsToShowRaw = useMemo(() => {
//         let filtered = baseQuestions;

//         if (showOnly === "Important") {
//             filtered = filtered.filter(q => importantQuestions.includes(q.id));
//         }

//         if (selectedTag !== "All") {
//             filtered = filtered.filter(q =>
//                 q.tags && q.tags.includes(selectedTag)
//             );
//         }

//         return filtered;
//     }, [baseQuestions, showOnly, importantQuestions, selectedTag]);

//     // Tags are derived from `baseQuestions` (English only)
//     useEffect(() => {
//         if (baseQuestions.length > 0) {
//             const tags = new Set();
//             baseQuestions.forEach(q => {
//                 if (q.tags && Array.isArray(q.tags)) {
//                     q.tags.forEach(tag => tags.add(tag));
//                 }
//             });
//             setAvailableTags(["All", ...Array.from(tags).sort()]);
//         }
//     }, [baseQuestions]);

//     const filteredTags = useMemo(() => {
//         if (!availableTags.length) return [];
//         if (tagSearchQuery.trim() === '') return availableTags;
//         return availableTags.filter(tag =>
//             tag.toLowerCase().includes(tagSearchQuery.toLowerCase())
//         );
//     }, [availableTags, tagSearchQuery]);

//     useEffect(() => {
//         const style = document.createElement('style');
//         style.innerHTML = `
//             @media (max-width: 768px) {
//                .hide-on-mobile {
//                     display: none!important;
//                 }
//                .show-on-mobile {
//                     display: block!important;
//                 }
//             }
//             @media (min-width: 769px) {
//                .show-on-mobile {
//                     display: none!important;
//                 }
//             }
//         `;
//         document.head.appendChild(style);
//         return () => {
//             document.head.removeChild(style);
//         };
//     }, []);

//     return (
//         <div style={{ padding: '20px', minHeight: "100vh", overflowY: 'auto' }}>
//             {selectedCategory && (
//                 <QuizCategorySEO
//                     category={selectedCategory?.category}
//                     imageURL={selectedCategory?.imageURL}
//                     quizzes={selectedCategory?.sets.flatMap((set) => set.quizzes)}
//                 />
//             )}

//             {showHeading && (
//                 <div className="t-heading" style={{ marginBottom: "20px" }}>
//                     <span>SSC CGL Exam </span>
//                     <span>Practice MCQ Quiz </span>
//                     <span>Topic-Wise Questions for </span>
//                     <span>CGL, MTS, CHSL</span>
//                 </div>
//             )}

//             {showSubHeading && (
//                 <div className="t-heading" style={{ marginBottom: "20px" }}>
//                     <span>Practice MCQ Quiz </span>
//                     <span>{quizType ? `${quizType.replace(/-/g, ' ').toUpperCase()}` : 'Select a Quiz:'}</span>
//                     <span> Topic-Wise Questions.</span>
//                 </div>
//             )}

//             {!quizType && (
//                 <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px', flexWrap: "wrap" }}>
//                     {allData.map((quiz) => (
//                         <div
//                             key={quiz.category}
//                             onClick={() => navigate(`/quizCategory/${quiz.category}`)}
//                             style={{
//                                 cursor: 'pointer',
//                                 border: '1px solid #ccc',
//                                 borderRadius: '10px',
//                                 width: '200px',
//                                 textAlign: 'center',
//                                 padding: '10px',
//                                 boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
//                             }}
//                         >
//                             <img
//                                 src={quiz.imageURL}
//                                 alt={quiz.category}
//                                 style={{ width: '100%', borderRadius: '10px' }}
//                             />
//                             <p style={{ fontWeight: 'bold', marginTop: '10px' }}>{quiz.category.replace(/-/g, ' ').toUpperCase()}</p>
//                         </div>
//                     ))}
//                 </div>
//             )}

//             {quizType && quizSet === undefined && selectedCategory && (
//                 <div>
//                     <div style={styles.filterContainer}>
//                         {recommendedSearches.map((filter) => (
//                             <button
//                                 key={filter}
//                                 onClick={() => setActiveFilter(filter)}
//                                 style={{
//                                     ...styles.filterButton,
//                                     ...(activeFilter === filter ? styles.activeFilter : {})
//                                 }}
//                             >
//                                 {filter}
//                             </button>
//                         ))}
//                     </div>
//                     {availableTags.length > 1 && ( // Using availableTags for search chips as well now
//                         <div style={styles.searchContainer}>
//                             <input
//                                 type="text"
//                                 placeholder="Search quiz sets..."
//                                 value={searchQuery}
//                                 onChange={(e) => {
//                                     setSearchQuery(e.target.value);
//                                     if (e.target.value.trim() === '') {
//                                         setShowRecommendations(true);
//                                     } else {
//                                         setShowRecommendations(false);
//                                     }
//                                 }}
//                                 onFocus={() => setShowRecommendations(true)}
//                                 onBlur={() => setTimeout(() => setShowRecommendations(false), 200)}
//                                 style={styles.searchInput}
//                             />
//                             {showRecommendations && recommendedSearches.length > 0 && (
//                                 <div style={styles.recommendationsContainer}>
//                                     {recommendedSearches.map((term, index) => (
//                                         <div
//                                             key={index}
//                                             onClick={() => setSearchQuery(term)}
//                                             style={styles.recommendationItem}
//                                         >
//                                             {term}
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}
//                         </div>
//                     )}
//                 </div>
//             )}

//             {quizType && quizSet === undefined && selectedCategory && (
//                 <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px', flexWrap: "wrap" }}>
//                     <QuizCategorySEO
//                         category={selectedCategory?.category}
//                         imageURL={selectedCategory?.imageURL}
//                         quizzes={selectedCategory?.sets.flatMap((set) => set.quizzes)}
//                     />
//                     {filteredSets.length === 0 ? (
//                         <p style={{
//                             fontSize: "18px",
//                             fontWeight: "bold",
//                             color: "#555",
//                             textAlign: "center",
//                             width: "100%",
//                             marginTop: "20px"
//                         }}>
//                             ❌ No results found for "{activeFilter}"
//                         </p>
//                     ) : (filteredSets.map((set) => {
//                         const isPremium = set.subscriptionRequired;
//                         return (
//                             <div
//                                 key={set.set}
//                                 onClick={() => {
//                                     if (set.authRequired && !user) {
//                                         navigate('/login');
//                                         return;
//                                     }
//                                     if (set.subscriptionRequired && !isSubscriptionActive) {
//                                         if (window.confirm("🔒 This premium quiz requires subscription. Subscribe now to access?")) {
//                                             navigate('/subscription');
//                                         }
//                                         return;
//                                     }
//                                     navigate(`/quizCategory/${quizType}/${set.set}`);
//                                 }}
//                                 style={{
//                                     cursor: (isPremium && !isSubscriptionActive) ? 'not-allowed' : 'pointer',
//                                     border: isPremium ? '2px solid gold' : '1px solid #ccc',
//                                     borderRadius: '10px',
//                                     width: '200px',
//                                     textAlign: 'center',
//                                     padding: '10px',
//                                     boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
//                                     position: 'relative',
//                                     backgroundColor: isPremium && !isSubscriptionActive ? '#f8f9fa' : '#fff',
//                                     opacity: isPremium && !isSubscriptionActive ? 0.7 : 1
//                                 }}
//                             >
//                                 {isPremium && (
//                                     <span style={{
//                                         position: 'absolute',
//                                         top: '-8px',
//                                         right: '-8px',
//                                         backgroundColor: 'gold',
//                                         color: 'black',
//                                         padding: '2px 8px',
//                                         borderRadius: '12px',
//                                         fontSize: '10px',
//                                         fontWeight: 'bold'
//                                     }}>
//                                         PREMIUM
//                                     </span>
//                                 )}
//                                 <p style={{
//                                     fontWeight: 'bold',
//                                     marginTop: '10px',
//                                     color: isPremium && !isSubscriptionActive ? '#6c757d' : '#000'
//                                 }}>
//                                     {set.title} (Questions: {set.quizzes.length})
//                                 </p>
//                                 {set.authRequired && !user && (
//                                     <p style={{ color: 'red', fontSize: '0.9em', marginTop: '5px' }}>
//                                         Please login to access.
//                                     </p>
//                                 )}
//                                 {isPremium && !isSubscriptionActive && (
//                                     <div style={{ marginTop: '5px' }}>
//                                         <span style={{ color: '#dc3545', fontSize: '12px' }}>🔒 Subscription Required</span>
//                                     </div>
//                                 )}
//                                 <p>
//                                     {user && updatedCategory?.sets?.find(s => s.set === set.set)?.attempted && (
//                                         <p>
//                                             <span style={{ color: "green", fontSize: "14px" }}>
//                                                 Completed! Score: <span style={{ color: "green", fontSize: "15px", fontWeight: "bold" }}>
//                                                     {updatedCategory?.sets?.find(s => s.set === set.set)?.lastScore}
//                                                 </span>
//                                             </span>
//                                         </p>
//                                     )}
//                                 </p>
//                             </div>
//                         );
//                     }))}
//                 </div>
//             )}

//             {selectedSet && (
//                 <div>
//                     {/* Filter buttons - All, Important, and Language options */}
//                     <div style={styles.filterContainer}>
//                         <button
//                             onClick={() => setShowOnly("All")}
//                             style={{
//                                 padding: '8px 16px',
//                                 borderRadius: '20px',
//                                 border: '1px solid #ddd',
//                                 background: showOnly === "All" ? '#007bff' : '#f5f5f5',
//                                 color: showOnly === "All" ? 'white' : '#333',
//                                 cursor: 'pointer',
//                                 fontSize: '14px'
//                             }}
//                         >
//                             All
//                         </button>
//                         <button
//                             onClick={() => {
//                                 if (!user) {
//                                     alert("Please login to see marked questions!");
//                                     return;
//                                 }
//                                 if (importantQuestions.length === 0) {
//                                     alert("No marked questions found. Please mark questions (★) to see them here.");
//                                     return;
//                                 }
//                                 setShowOnly("Important")
//                             }}
//                             style={{
//                                 padding: '8px 16px',
//                                 borderRadius: '20px',
//                                 border: '1px solid #ddd',
//                                 background: showOnly === "Important" ? '#007bff' : '#f5f5f5',
//                                 color: showOnly === "Important" ? 'white' : '#333',
//                                 cursor: 'pointer',
//                                 fontSize: '14px'
//                             }}
//                         >
//                             Important ({importantQuestions.length})
//                         </button>

//                         {/* Language Selection Buttons */}
//                         {selectedSet.quizzesHin && (
//                             <>
//                                 <button
//                                     onClick={() => setLanguage('en')}
//                                     style={{
//                                         padding: '8px 16px',
//                                         borderRadius: '20px',
//                                         border: '1px solid #ddd',
//                                         background: language === 'en' ? '#28a745' : '#f5f5f5',
//                                         color: language === 'en' ? 'white' : '#333',
//                                         cursor: 'pointer',
//                                         fontSize: '14px',
//                                         marginLeft: '10px'
//                                     }}
//                                 >
//                                     English
//                                 </button>
//                                 <button
//                                     onClick={() => setLanguage('hi')}
//                                     style={{
//                                         padding: '8px 16px',
//                                         borderRadius: '20px',
//                                         border: '1px solid #ddd',
//                                         background: language === 'hi' ? '#28a745' : '#f5f5f5',
//                                         color: language === 'hi' ? 'white' : '#333',
//                                         cursor: 'pointer',
//                                         fontSize: '14px'
//                                     }}
//                                 >
//                                     Hindi
//                                 </button>
//                                 <button
//                                     onClick={() => setLanguage('bilingual')}
//                                     style={{
//                                         padding: '8px 16px',
//                                         borderRadius: '20px',
//                                         border: '1px solid #ddd',
//                                         background: language === 'bilingual' ? '#28a745' : '#f5f5f5',
//                                         color: language === 'bilingual' ? 'white' : '#333',
//                                         cursor: 'pointer',
//                                         fontSize: '14px'
//                                     }}
//                                 >
//                                     Bilingual
//                                 </button>
//                             </>
//                         )}
//                     </div>

//                     {/* Tag filter chips */}
//                     {availableTags.length > 1 && (
//                         <div style={{ ...styles.filterContainer, marginTop: '10px', marginBottom: '20px' }} className="hide-on-mobile">
//                             {availableTags.map((tag) => (
//                                 <button
//                                     key={tag}
//                                     onClick={() => setSelectedTag(tag)}
//                                     style={{
//                                         padding: '6px 14px',
//                                         borderRadius: '20px',
//                                         border: '1px solid #ddd',
//                                         background: selectedTag === tag ? '#28a745' : '#f5f5f5',
//                                         color: selectedTag === tag ? 'white' : '#333',
//                                         cursor: 'pointer',
//                                         fontSize: '13px',
//                                         transition: 'all 0.3s ease',
//                                     }}
//                                 >
//                                     {tag} {tag !== "All" && `(${baseQuestions.filter(q => q.tags?.includes(tag)).length})`}
//                                 </button>
//                             ))}
//                         </div>
//                     )}

//                     {/* Tag Search Container for Mobile */}
//                     {availableTags.length > 1 && (
//                         <div style={styles.searchContainer} className="show-on-mobile">
//                             <input
//                                 type="text"
//                                 placeholder="Search topics/tags..."
//                                 value={tagSearchQuery}
//                                 onChange={(e) => {
//                                     setTagSearchQuery(e.target.value);
//                                     if (e.target.value.trim() === '') {
//                                         setShowTagRecommendations(true);
//                                     } else {
//                                         setShowTagRecommendations(false);
//                                     }
//                                 }}
//                                 onFocus={() => setShowTagRecommendations(true)}
//                                 onBlur={() => setTimeout(() => setShowTagRecommendations(false), 200)}
//                                 style={styles.searchInput}
//                             />
//                             {showTagRecommendations && filteredTags.length > 0 && (
//                                 <div style={styles.recommendationsContainer}>
//                                     {filteredTags.map((tag, index) => (
//                                         <div
//                                             key={index}
//                                             onClick={() => {
//                                                 setSelectedTag(tag);
//                                                 setTagSearchQuery(tag);
//                                                 setShowTagRecommendations(false);
//                                             }}
//                                             style={{
//                                                 ...styles.recommendationItem,
//                                                 ...(selectedTag === tag ? { backgroundColor: '#007bff', color: 'white' } : {})
//                                             }}
//                                             onMouseEnter={(e) => {
//                                                 e.target.style.backgroundColor = '#007bff';
//                                                 e.target.style.color = 'white';
//                                             }}
//                                             onMouseLeave={(e) => {
//                                                 if (selectedTag !== tag) {
//                                                     e.target.style.backgroundColor = '#fff';
//                                                     e.target.style.color = '#333';
//                                                 }
//                                             }}
//                                         >
//                                             {tag} {tag !== "All" && `(${baseQuestions.filter(q => q.tags?.includes(tag)).length})`}
//                                             {selectedTag === tag && ' ✓'}
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}
//                         </div>
//                     )}

//                     {/* Questions Display */}
//                     {questionsToShowRaw.map((question, index) => {
//                         const displayQuestion = getLocalizedQuestion(question);

//                         return (
//                             <div key={question.id} id={`question-${question.id}`} style={{ marginBottom: '20px' }}>
//                                 <h3 style={{ whiteSpace: "pre-line" }}>
//                                     {index + 1}. {displayQuestion.question}
//                                     <span style={{ fontWeight: "bold" }}>{question?.exam}</span>
//                                     <button
//                                         onClick={async () => {
//                                             if (!user) {
//                                                 alert("Please login to mark questions!");
//                                                 return;
//                                             }
//                                             const updatedImportantQuestions = importantQuestions.includes(question.id)
//                                                 ? importantQuestions.filter(id => id !== question.id)
//                                                 : [...importantQuestions, question.id];
//                                             setImportantQuestions(updatedImportantQuestions);
//                                             await saveImportantQuestions(updatedImportantQuestions);
//                                             if (updatedImportantQuestions.length === 0) {
//                                                 setShowOnly("All");
//                                             }
//                                         }}
//                                         style={{
//                                             background: 'none',
//                                             border: 'none',
//                                             fontSize: '24px',
//                                             cursor: 'pointer',
//                                             color: importantQuestions.includes(question.id) ? 'gold' : '#ccc'
//                                         }}
//                                     >
//                                         {importantQuestions.includes(question.id) ? '★' : '☆'}
//                                     </button>
//                                 </h3>
//                                 <ul>
//                                     {/* ************* MODIFIED ************* */}
//                                     {displayQuestion.options.map((option, shuffledIndex) => {
//                                         // Determine if this displayed option is the one the user selected for bolding and border
//                                         const isSelectedForDisplay = selectedOptions[question.id] !== undefined &&
//                                             getEnglishTextForShuffledOption(question.id, shuffledIndex) === selectedOptions[question.id];

//                                         return (
//                                             <li
//                                                 key={option}
//                                                 // Pass the shuffledIndex to handleOptionClick
//                                                 onClick={() => handleOptionClick(question.id, shuffledIndex)}
//                                                 style={{
//                                                     padding: '10px',
//                                                     margin: '5px 0',
//                                                     cursor: selectedOptions[question.id] !== undefined ? 'not-allowed' : 'pointer',
//                                                     // Pass the shuffledIndex to getOptionStyle
//                                                     ...getOptionStyle(question.id, shuffledIndex),
//                                                     fontWeight: isSelectedForDisplay ? 'bold' : 'normal',
//                                                     // border: isSelectedForDisplay ? '2px solid #007bff' : 'none'
//                                                 }}
//                                             >
//                                                 {option}
//                                             </li>
//                                         );
//                                     })}
//                                 </ul>
//                                 <div style={{ display: "flex", flexDirection: "row" }}>
//                                     <button
//                                         onClick={() => {
//                                             if (!user) {
//                                                 alert("Please login to download the image!");
//                                                 return;
//                                             }
//                                             handleShareClick(question)
//                                         }}
//                                         className="button"
//                                         style={{ fontSize: "small" }}
//                                     >
//                                         Image <FaDownload size={10} />
//                                     </button>
//                                     <button
//                                         onClick={() => {
//                                             if (!user) {
//                                                 alert("Please login to download the video!");
//                                                 return;
//                                             }
//                                             handleVideoShareClick(question)
//                                         }}
//                                         className="button"
//                                         style={{ marginLeft: "5px", fontSize: "small" }}
//                                     >
//                                         Video <FaDownload size={10} />
//                                     </button>
//                                     {question.explanation && (
//                                         <button
//                                             onClick={() => {
//                                                 if (!user) {
//                                                     alert("Please login to see the explanation!");
//                                                     return;
//                                                 }
//                                                 if (selectedOptions[question.id] !== undefined) { // Check if an option has been selected
//                                                     handleExplanationClick(question);
//                                                 } else {
//                                                     alert("Please select an option to see the explanation!");
//                                                 }
//                                             }}
//                                             className="button"
//                                             style={{
//                                                 marginLeft: "5px",
//                                                 fontSize: "small",
//                                                 cursor: selectedOptions[question.id] !== undefined ? "pointer" : "not-allowed",
//                                                 opacity: selectedOptions[question.id] !== undefined ? 1 : 0.6,
//                                             }}
//                                         >
//                                             Explain
//                                         </button>
//                                     )}
//                                 </div>
//                             </div>
//                         );
//                     })}

//                     {showOnly === "All" && selectedTag === "All" && (
//                         <button className="button" onClick={calculateScore}>Submit</button>
//                     )}

//                     {showScore && showOnly === "All" && (
//                         <div>
//                             <h2>Your Score: {score} out of {selectedSet.quizzes.length}</h2>
//                             <h2>Unattempted: {unattempted}</h2>
//                             <button className="button" onClick={handleRefresh}>Go Back</button>
//                         </div>
//                     )}

//                     {showPopup && activeQuestion && (
//                         <SharePopup
//                             question={activeQuestion}
//                             onClose={() => setShowPopup(false)}
//                         />
//                     )}

//                     {showVideoPopup && activeVideoQuestion && (
//                         <SharePopupWithVideo
//                             question={activeVideoQuestion}
//                             onClose={() => setShowVideoPopup(false)}
//                         />
//                     )}

//                     {showExplanationPopup && activeExplanationQuestion && (
//                         <ExplanationPopup
//                             question={activeExplanationQuestion}
//                             onClose={() => setShowExplanationPopup(false)}
//                         />
//                     )}
//                 </div>
//             )}
//         </div>
//     );
// }

// const styles = {
//     filterContainer: {
//         display: 'flex',
//         flexWrap: 'wrap',
//         gap: '10px',
//         marginBottom: '20px',
//     },
//     filterButton: {
//         padding: '8px 16px',
//         borderRadius: '20px',
//         border: '1px solid #ddd',
//         background: '#f5f5f5',
//         cursor: 'pointer',
//         fontSize: '14px',
//         transition: 'all 0.3s ease',
//     },
//     activeFilter: {
//         background: '#007bff',
//         color: 'white',
//         borderColor: '#007bff',
//     },
//     searchContainer: {
//         display: "flex",
//         flexDirection: "column",
//         alignItems: "center",
//         position: "relative",
//         marginBottom: "20px",
//         width: "100%",
//     },
//     searchInput: {
//         padding: "12px",
//         width: "80%",
//         borderRadius: "8px",
//         border: "2px solid #007bff",
//         fontSize: "16px",
//         outline: "none",
//         transition: "0.3s",
//         boxShadow: "0 2px 5px rgba(0, 123, 255, 0.2)"
//     },
//     recommendationsContainer: {
//         position: "absolute",
//         width: "80%",
//         background: "#fff",
//         border: "1px solid #ddd",
//         borderRadius: "8px",
//         boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
//         marginTop: "50px",
//         zIndex: 10,
//         overflowY: "auto",
//         maxHeight: "200px",
//     },
//     recommendationItem: {
//         padding: "12px",
//         cursor: "pointer",
//         fontSize: "14px",
//         borderBottom: "1px solid #f0f0f0",
//         transition: "0.3s",
//         textAlign: "left",
//         color: "#333",
//         backgroundColor: "#fff"
//     },
// };

// export default DummyPage;

//OLDER VERSION 1
// import React, { useState, useContext, useMemo, useEffect } from 'react';
// import { useParams, useNavigate, useLocation } from 'react-router-dom';
// // import quizData from "../data/quizzes.json"; // Import your JSON data
// import allData from "../data/allData";// Import your JSON data
// import { themeContext } from '../Context';
// import { auth } from '../firebase/firebaseConfig';
// import QuizCategorySEO from '../utilities/SEO/QuizCategorySEO';
// import SharePopup from '../utilities/SharePopup';
// import SharePopupWithVideo from '../utilities/SharePopupWithVideo';
// import ExplanationPopup from '../utilities/ExplanationPopup';
// import { saveQuizAttempt } from '../utilities/SaveQuizAttempt';
// import { doc, getDoc, setDoc } from "firebase/firestore";
// import { db } from '../firebase/firebaseConfig';
// import { FaDownload } from 'react-icons/fa';

// //SUBS
// import { useSubscription } from '../utilities/SubscriptionContext';

// function DummyPage() {
//     const { quizType, quizSet } = useParams();
//     const navigate = useNavigate();
//     const location = useLocation();
//     const [selectedOptions, setSelectedOptions] = useState({});
//     const [showScore, setShowScore] = useState(false);
//     const [score, setScore] = useState(0);
//     const [unattempted, setUnattempted] = useState(0);
//     const [user, setUser] = useState(null);
//     const [authLoading, setAuthLoading] = useState(true);

//     // State to Manage Popup Visibility
//     const [showPopup, setShowPopup] = useState(false);
//     const [activeQuestion, setActiveQuestion] = useState(null);
//     const [activeVideoQuestion, setActiveVideoQuestion] = useState(null);

//     // State to Manage Popup Visibility
//     const [showVideoPopup, setShowVideoPopup] = useState(false);

//     //MARKING IMPORTANT QUESTIONS
//     const [importantQuestions, setImportantQuestions] = useState([]);
//     const [showOnly, setShowOnly] = useState("All");
//     //Search Chips
//     const [activeFilter, setActiveFilter] = useState("All");

//     //SUBS
//     const { isSubscriptionActive, loading } = useSubscription();
//     //TAGS
//     // Add this with your other state declarations
//     const [selectedTag, setSelectedTag] = useState("All");
//     const [availableTags, setAvailableTags] = useState([]);
//     const [tagSearchQuery, setTagSearchQuery] = useState('');
//     const [showTagRecommendations, setShowTagRecommendations] = useState(false);

//     // Handler to Open Popup
//     const handleShareClick = (question) => {
//         setActiveQuestion(question);
//         setShowPopup(true);
//         // console.log("question", question)
//     };

//     // Handler to Open Popup
//     const handleVideoShareClick = (question) => {
//         setActiveVideoQuestion(question);
//         setShowVideoPopup(true);
//         // console.log("question", question)
//     };

//     // Explanation Declarations
//     const [showExplanationPopup, setShowExplanationPopup] = useState(false);
//     const [activeExplanationQuestion, setActiveExplanationQuestion] = useState(null);

//     // Add this handler function
//     const handleExplanationClick = (question) => {
//         if (!user) {
//             alert("Please login to see the explanation!");
//             return;
//         }
//         setActiveExplanationQuestion(question);
//         setShowExplanationPopup(true);
//     };

//     const theme = useContext(themeContext);
//     const darkMode = theme.state.darkMode;

//     // Effect to track the authentication state
//     useEffect(() => {
//         const unsubscribe = auth.onAuthStateChanged(currentUser => {
//             setUser(currentUser);
//         });
//         return () => unsubscribe(); // Unsubscribe on component unmount
//     }, []);



//     // Determine if we should display the header based on the current path
//     const showHeading = location.pathname === `/quizCategory` || location.pathname.endsWith('/quizcategory/');
//     // const showSubHeading = location.pathname.includes('/quizCategory/');
//     const showSubHeading = location.pathname.startsWith('/quizCategory/') && location.pathname !== '/quizCategory/';
//     // const showHeading = !showSubHeading && (location.pathname === '/quizCategory' || location.pathname === '/quizCategory/');



//     // Find the quizzes for the selected quiz type and set
//     // const selectedCategory = quizData.categories.find(category => category.category === quizType);
//     // console.log("selectedCategory", allData);
//     // console.log("selectedCategory2", quizData.categories);

//     const selectedCategory = allData.find(category => category.category === quizType);
//     // const selectedSet = selectedCategory && quizSet !== undefined ? selectedCategory.sets[parseInt(quizSet)] : null;
//     const selectedSet = selectedCategory?.sets.find(set => set.set === quizSet) || null;
//     // console.log("selectedCategory2", selectedCategory);

//     // Redirect if quiz set requires authentication and the user is not logged in
//     useEffect(() => {
//         const unsubscribe = auth.onAuthStateChanged(currentUser => {
//             setUser(currentUser);
//             setAuthLoading(false); // done checking
//         });
//         return () => unsubscribe();
//     }, []);
//     useEffect(() => {
//         if (!authLoading && selectedSet && selectedSet.authRequired && !user) {
//             navigate('/login'); // Redirect to login page
//         }
//     }, [selectedSet, user, navigate, authLoading]);

//     useEffect(() => {
//         // Check if directly accessing a premium quiz set via URL
//         if (loading) return;

//         if (selectedSet && selectedSet.subscriptionRequired && !isSubscriptionActive) {
//             alert("This premium quiz requires subscription. Redirecting to subscription page.");
//             navigate('/subscription');
//             return;
//         }
//     }, [selectedSet, isSubscriptionActive, navigate, loading]);



//     // Handle option selection using question ID
//     // const handleOptionClick = (questionId, option) => {
//     //     setSelectedOptions({
//     //         ...selectedOptions,
//     //         [questionId]: option,
//     //     });
//     // };
//     const handleOptionClick = (questionId, option) => {
//         if (selectedOptions[questionId]) return; // Prevent changing the selection

//         setSelectedOptions(prevOptions => ({
//             ...prevOptions,
//             [questionId]: option,
//         }));
//     };



//     const shuffledQuestions = useMemo(() => {
//         return selectedSet
//             ? selectedSet.quizzes.map(question => ({
//                 ...question,
//                 options: [...question.options].sort(() => Math.random() - 0.5),
//             }))
//             : [];
//     }, [selectedSet]);



//     // useEffect(() => {
//     //     console.log("shuffledQuestions", shuffledQuestions);
//     // }, [shuffledQuestions]); // This logs only once on mount

//     // Calculate the user's score
//     // const calculateScore = () => {
//     //     let correctAnswers = 0;
//     //     let unattemptedCount = 0;

//     //     const questions = selectedSet ? selectedSet.quizzes : [];
//     //     questions.forEach((question) => {
//     //         const userAnswer = selectedOptions[question.id];
//     //         if (userAnswer === question.correctAnswer) {
//     //             correctAnswers += 1;
//     //         } else if (!userAnswer) {
//     //             unattemptedCount += 1;
//     //         }
//     //     });

//     //     setScore(correctAnswers);
//     //     setUnattempted(unattemptedCount);
//     //     setShowScore(true);
//     // };
//     const calculateScore = async () => {
//         let correctAnswers = 0;
//         let unattemptedCount = 0;

//         const questions = selectedSet ? selectedSet.quizzes : [];
//         questions.forEach((question) => {
//             const userAnswer = selectedOptions[question.id];
//             if (userAnswer === question.correctAnswer) {
//                 correctAnswers += 1;
//             } else if (!userAnswer) {
//                 unattemptedCount += 1;
//             }
//         });

//         const attemptData = {
//             score: correctAnswers,
//             unattempted: unattemptedCount,
//             totalQuestions: questions.length,
//             timestamp: new Date().toISOString(),
//             importantQuestions: importantQuestions // Add this line
//         };

//         setScore(correctAnswers);
//         setUnattempted(unattemptedCount);
//         setShowScore(true);

//         // Save to Firestore
//         if (user) {
//             await saveQuizAttempt(user.uid, quizType, quizSet, attemptData);
//             alert("Quiz attempt saved successfully!");
//         } else {
//             alert("Please log in to save your quiz progress.");
//         }

//     };

//     // MARK IMPORTANT QUESTIONS
//     const saveImportantQuestions = async (questionsToSave) => {
//         if (!user || !quizType || quizSet === undefined) return;
//         // console.log("questionsToSave", questionsToSave);
//         const userRef = doc(db, "users", user.uid);
//         await setDoc(userRef, {
//             quizAttempts: {
//                 [quizType]: {
//                     [quizSet]: {
//                         importantQuestions: questionsToSave
//                     }
//                 }
//             }
//         }, { merge: true }); // Crucial: This preserves other data
//     };

//     const [updatedCategory, setUpdatedCategory] = useState(selectedCategory);

//     useEffect(() => {
//         const fetchUserQuizAttempts = async () => {
//             if (user) {
//                 const userDocRef = doc(db, "users", user.uid);
//                 const userDoc = await getDoc(userDocRef);

//                 if (userDoc.exists()) {
//                     const quizAttempts = userDoc.data().quizAttempts || {};

//                     // Load important questions for current quiz set
//                     if (quizSet !== undefined && quizAttempts[quizType]?.[quizSet]) {
//                         setImportantQuestions(quizAttempts[quizType][quizSet].importantQuestions || []);
//                     }

//                     // Create a new copy of selectedCategory
//                     // const updatedSets = selectedCategory?.sets.map((set, index) => {
//                     //     if (quizAttempts[quizType]?.[index]) {
//                     //         return {
//                     //             ...set,
//                     //             attempted: true,
//                     //             lastScore: quizAttempts[quizType][index].score,
//                     //             // importantCount: quizAttempts[quizType][index].importantQuestions?.length || 0
//                     //         };
//                     //     }
//                     //     return set;
//                     // });
//                     const updatedSets = selectedCategory?.sets.map(set => ({
//                         ...set,
//                         attempted: quizAttempts[quizType]?.[set.set] !== undefined,
//                         lastScore: quizAttempts[quizType]?.[set.set]?.score
//                     }));
//                     // Update the state with the new copy
//                     setUpdatedCategory({
//                         ...selectedCategory,
//                         sets: updatedSets,
//                     });

//                 }
//             }
//         };

//         fetchUserQuizAttempts();
//     }, [user, quizType, quizSet, selectedCategory, location.pathname]);

//     // console.log("updatedCategory", updatedCategory);

//     // Style for answers
//     const getOptionStyle = (questionId, option) => {
//         if (!selectedSet) return {};
//         const question = selectedSet.quizzes.find(q => q.id === questionId);
//         const correctAnswer = question.correctAnswer;
//         const selectedOption = selectedOptions[questionId];

//         if (option === correctAnswer && selectedOption === option) {
//             return { backgroundColor: 'lightgreen', color: "black" };
//         }
//         if (option === selectedOption && option !== correctAnswer) {
//             return { backgroundColor: 'lightcoral', color: "black" };
//         }
//         if (selectedOption && correctAnswer === option) {
//             return { backgroundColor: 'lightgreen', color: "black" };
//         }
//         return { backgroundColor: darkMode ? "rgb(128 128 128 / 42%)" : '#f0f0f0' };
//     };

//     // Handle Refresh
//     const handleRefresh = () => {
//         // navigate('/quizCategory');
//         navigate(`/quizCategory/${quizType}`);
//         setSelectedOptions({});
//         setShowScore(false);
//         setUnattempted(0);
//     };

//     //HANDLE SEARCH IN QUIZ PAGE
//     const recommendedSearchesMap = {
//         "current-affairs-sscvidya": ["All", "September"],
//         "static-gk": ["All", "MTS 2024", "Census", "Folk Dances", "Olympic", "Books", "Sports", "Gharana", "Songs", "Dance", "Fairs", "Festivals", "Institutes", "Schemes"],
//         "history": ["All", "CGL 2024", "MTS 2024", "Mordern", "SSCVIDYA"],
//         "polity": ["All", "MTS 2024", "CGL 2024"],
//         "geography": ["All", "CGL 2024", "MTS 2024", "sscvidya", "Origin of Universe", "Solar System", "Earth", "Tectonic Plates", "Indian Mountains", "Rocks", "Atmosphere", "Seasons in India", "Borders of India", "Physiographic Divisions", "Himalayan", "Ports in India", "Rivers of India", "Waterfalls", "Waterways"],
//         "current-affairs-pyq": ["All", "MTS 2024"],
//         "english": ["All", "VOCAB", "IDIOMS"],
//         "explore-more": ["All", "SSC 2025", "MTS 2024", "Delhi Police 2023"]
//         // Add more categories as needed 
//     };
//     const [searchQuery, setSearchQuery] = useState('');
//     const [showRecommendations, setShowRecommendations] = useState(false);


//     // Extract quiz category from URL
//     const categoryFromUrl = location.pathname.split("/")[2]; // Assuming URL format: /quizCategory/:category
//     // Get recommended searches based on the extracted category
//     const recommendedSearches = recommendedSearchesMap[categoryFromUrl] || ["All"];
//     // Filter quiz sets based on the search query
//     // const filteredSets = selectedCategory?.sets.filter((set) =>
//     //     set.title.toLowerCase().includes(searchQuery.toLowerCase())
//     // );

//     // const filteredSets = selectedCategory?.sets
//     //     .map((set, index) => ({ ...set, originalIndex: index })) // Keep original index
//     //     .filter((set) => set.title.toLowerCase().includes(searchQuery.toLowerCase()));
//     // const filteredSets = selectedCategory?.sets
//     //     .map((set, index) => ({ ...set, originalIndex: index }))
//     //     .filter(set => {
//     //         if (activeFilter === "All") return true;
//     //         return set.title.toLowerCase().includes(activeFilter.toLowerCase());
//     //     })
//     //     .filter(set => {
//     //         if (searchQuery.trim() === "") return true;
//     //         return set.title.toLowerCase().includes(searchQuery.toLowerCase());
//     //     });

//     // PREV VERSION OF filteredSets
//     // const filteredSets = selectedCategory?.sets
//     //     .filter(set => {
//     //         if (activeFilter === "All") return true;
//     //         return set.title.toLowerCase().includes(activeFilter.toLowerCase());
//     //     })
//     //     .filter(set => {
//     //         if (searchQuery.trim() === "") return true;
//     //         return set.title.toLowerCase().includes(searchQuery.toLowerCase());
//     //     });

//     const filteredSets = useMemo(() => {
//         return selectedCategory?.sets.filter(set => {
//             if (activeFilter === "All") return true;
//             return set.title.toLowerCase().includes(activeFilter.toLowerCase());
//         })
//             .filter(set => {
//                 if (searchQuery.trim() === "") return true;
//                 return set.title.toLowerCase().includes(searchQuery.toLowerCase());
//             }) || [];
//     }, [selectedCategory, activeFilter, searchQuery]);

//     // console.log("filteredSets:", filteredSets);
//     const categoryFromUrl2 = location.pathname.split("/")[3]; // Assuming URL format: /quizCategory/:category

//     useEffect(() => {
//         setSearchQuery(""); // Reset search query when category changes
//         setShowOnly("All")
//         setImportantQuestions([])
//         setShowScore(false);
//         setTagSearchQuery("")
//         setAvailableTags("")
//     }, [categoryFromUrl2, categoryFromUrl]); // Runs when category changes

//     useEffect(() => {
//         setActiveFilter("All")
//     }, [categoryFromUrl])

//     // Extract unique tags from questions
//     useEffect(() => {
//         if (shuffledQuestions.length > 0) {
//             const tags = new Set();
//             shuffledQuestions.forEach(q => {
//                 if (q.tags && Array.isArray(q.tags)) {
//                     q.tags.forEach(tag => tags.add(tag));
//                 }
//             });
//             setAvailableTags(["All", ...Array.from(tags).sort()]);
//         }
//     }, [shuffledQuestions, categoryFromUrl2, categoryFromUrl]);



//     // Calculate filtered questions before rendering
//     // const questionsToShow = showOnly === "Important"
//     //     ? shuffledQuestions.filter(q => importantQuestions.includes(q.id))
//     //     : shuffledQuestions;
//     // Update your questionsToShow calculation
//     const questionsToShow = useMemo(() => {
//         let filtered = shuffledQuestions;

//         // Filter by important
//         if (showOnly === "Important") {
//             filtered = filtered.filter(q => importantQuestions.includes(q.id));
//         }

//         // Filter by tag
//         if (selectedTag !== "All") {
//             filtered = filtered.filter(q =>
//                 q.tags && q.tags.includes(selectedTag)
//             );
//         }

//         return filtered;
//     }, [shuffledQuestions, showOnly, importantQuestions, selectedTag]);

//     // console.log("questionsToShow", importantQuestions);

//     // Filter tags based on search query
//     const filteredTags = useMemo(() => {
//         if (!availableTags.length) return [];
//         if (tagSearchQuery.trim() === '') return availableTags;
//         return availableTags.filter(tag =>
//             tag.toLowerCase().includes(tagSearchQuery.toLowerCase())
//         );
//     }, [availableTags, tagSearchQuery]);

//     // Add this at the top of your component
//     useEffect(() => {
//         const style = document.createElement('style');
//         style.innerHTML = `
//         @media (max-width: 768px) {
//             .hide-on-mobile {
//                 display: none !important;
//             }
//             .show-on-mobile {
//                 display: block !important;
//             }
//         }
//         @media (min-width: 769px) {
//             .show-on-mobile {
//                 display: none !important;
//             }
//         }
//     `;
//         document.head.appendChild(style);

//         return () => {
//             document.head.removeChild(style);
//         };
//     }, []);

//     return (
//         <div style={{ padding: '20px', minHeight: "100vh", overflowY: 'auto' }}>
//             {/* Helmet for setting the head elements */}
//             {selectedCategory && (
//                 <QuizCategorySEO
//                     category={selectedCategory?.category}
//                     imageURL={selectedCategory?.imageURL}
//                     quizzes={selectedCategory?.sets.flatMap((set) => set.quizzes)}
//                 />
//             )}

//             {/* Conditional Rendering of the Heading */}
//             {showHeading && (
//                 <div className="t-heading" style={{ marginBottom: "20px" }}>
//                     <span>SSC CGL Exam </span>
//                     <span>Practice MCQ Quiz </span>
//                     <span>Topic-Wise Questions for </span>
//                     <span>CGL, MTS, CHSL</span>
//                 </div>
//             )}
//             {/* <h1>{quizType ? `${quizType.charAt(0).toUpperCase() + quizType.slice(1)} Quiz` : 'Select a Quiz:'}</h1> */}
//             {showSubHeading && (
//                 <div className="t-heading" style={{ marginBottom: "20px" }}>
//                     <span>Practice MCQ Quiz </span>
//                     <span>{quizType ? `${quizType.replace(/-/g, ' ').toUpperCase()}` : 'Select a Quiz:'}</span>
//                     <span> Topic-Wise Questions.</span>
//                 </div>
//             )}
//             {/* Step 1: Quiz Type Selection */}
//             {!quizType && (
//                 <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px', flexWrap: "wrap" }}>

//                     {allData.map((quiz) => (
//                         <div
//                             key={quiz.category}
//                             onClick={() => navigate(`/quizCategory/${quiz.category}`)}
//                             style={{
//                                 cursor: 'pointer',
//                                 border: '1px solid #ccc',
//                                 borderRadius: '10px',
//                                 width: '200px',
//                                 textAlign: 'center',
//                                 padding: '10px',
//                                 boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
//                             }}
//                         >
//                             <img
//                                 src={quiz.imageURL}
//                                 alt={quiz.category}
//                                 style={{ width: '100%', borderRadius: '10px' }}
//                             />
//                             <p style={{ fontWeight: 'bold', marginTop: '10px' }}>{quiz.category.replace(/-/g, ' ').toUpperCase()}</p>
//                         </div>
//                     ))}
//                 </div>
//             )}

//             {/* Step 2: Quiz Set Selection */}
//             {/* Search Bar */}
//             {quizType && quizSet === undefined && selectedCategory && (
//                 <div>
//                     {/* Category filter chips */}
//                     <div style={styles.filterContainer}>
//                         {recommendedSearches.map((filter) => (
//                             <button
//                                 key={filter}
//                                 onClick={() => setActiveFilter(filter)}
//                                 style={{
//                                     ...styles.filterButton,
//                                     ...(activeFilter === filter ? styles.activeFilter : {})
//                                 }}
//                             >
//                                 {filter}
//                             </button>
//                         ))}
//                     </div>
//                     {availableTags.length > 1 && (
//                         <div style={styles.searchContainer}>
//                             <input
//                                 type="text"
//                                 placeholder="Search quiz sets..."
//                                 value={searchQuery}
//                                 // onChange={(e) => setSearchQuery(e.target.value)}
//                                 onChange={(e) => {
//                                     setSearchQuery(e.target.value);
//                                     if (e.target.value.trim() === '') {
//                                         setShowRecommendations(true);
//                                     } else {
//                                         setShowRecommendations(false);
//                                     }
//                                 }}
//                                 onFocus={() => setShowRecommendations(true)}
//                                 onBlur={() => setTimeout(() => setShowRecommendations(false), 200)}
//                                 style={styles.searchInput}
//                             />
//                             {/* Recommended searches */}
//                             {showRecommendations && recommendedSearches.length > 0 && (
//                                 <div style={styles.recommendationsContainer}>
//                                     {recommendedSearches.map((term, index) => (
//                                         <div
//                                             key={index}
//                                             onClick={() => setSearchQuery(term)}
//                                             style={styles.recommendationItem}
//                                         >
//                                             {term}
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}
//                         </div>)}
//                 </div>
//             )}
//             {/* Quiz List Display */}


//             {quizType && quizSet === undefined && selectedCategory && (
//                 <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px', flexWrap: "wrap" }}>
//                     <QuizCategorySEO
//                         category={selectedCategory?.category}
//                         imageURL={selectedCategory?.imageURL}
//                         quizzes={selectedCategory?.sets.flatMap((set) => set.quizzes)}
//                     />
//                     {
//                         filteredSets.length === 0 ? (
//                             <p style={{
//                                 fontSize: "18px",
//                                 fontWeight: "bold",
//                                 color: "#555",
//                                 textAlign: "center",
//                                 width: "100%",
//                                 marginTop: "20px"
//                             }}>
//                                 ❌ No results found for "{activeFilter}"
//                             </p>
//                         ) : (filteredSets.map((set) => {
//                             const isPremium = set.subscriptionRequired;

//                             return (
//                                 <div
//                                     key={set.set}
//                                     onClick={() => {
//                                         // First check if login is required
//                                         if (set.authRequired && !user) {
//                                             navigate('/login');
//                                             return;
//                                         }

//                                         // Then check if subscription is required
//                                         if (set.subscriptionRequired && !isSubscriptionActive) {
//                                             if (window.confirm("🔒 This premium quiz requires subscription. Subscribe now to access?")) {
//                                                 navigate('/subscription');
//                                             }
//                                             return;
//                                         }

//                                         navigate(`/quizCategory/${quizType}/${set.set}`);
//                                     }}
//                                     style={{
//                                         cursor: (isPremium && !isSubscriptionActive) ? 'not-allowed' : 'pointer',
//                                         border: isPremium ? '2px solid gold' : '1px solid #ccc',
//                                         borderRadius: '10px',
//                                         width: '200px',
//                                         textAlign: 'center',
//                                         padding: '10px',
//                                         boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
//                                         position: 'relative',
//                                         backgroundColor: isPremium && !isSubscriptionActive ? '#f8f9fa' : '#fff',
//                                         opacity: isPremium && !isSubscriptionActive ? 0.7 : 1
//                                     }}
//                                 >
//                                     {/* Premium badge */}
//                                     {isPremium && (
//                                         <span style={{
//                                             position: 'absolute',
//                                             top: '-8px',
//                                             right: '-8px',
//                                             backgroundColor: 'gold',
//                                             color: 'black',
//                                             padding: '2px 8px',
//                                             borderRadius: '12px',
//                                             fontSize: '10px',
//                                             fontWeight: 'bold'
//                                         }}>
//                                             PREMIUM
//                                         </span>
//                                     )}

//                                     <p style={{
//                                         fontWeight: 'bold',
//                                         marginTop: '10px',
//                                         color: isPremium && !isSubscriptionActive ? '#6c757d' : '#000'
//                                     }}>
//                                         {set.title} (Questions: {set.quizzes.length})
//                                     </p>

//                                     {/* Display login message if auth is required */}
//                                     {set.authRequired && !user && (
//                                         <p style={{ color: 'red', fontSize: '0.9em', marginTop: '5px' }}>
//                                             Please login to access.
//                                         </p>
//                                     )}

//                                     {/* Show lock icon for premium content without subscription */}
//                                     {isPremium && !isSubscriptionActive && (
//                                         <div style={{ marginTop: '5px' }}>
//                                             <span style={{ color: '#dc3545', fontSize: '12px' }}>🔒 Subscription Required</span>
//                                         </div>
//                                     )}

//                                     {/* Show completed status */}
//                                     <p>
//                                         {
//                                             user &&
//                                             updatedCategory?.sets?.find(s => s.set === set.set)?.attempted
//                                             && (
//                                                 <p>
//                                                     <span style={{ color: "green", fontSize: "14px" }}>
//                                                         Completed! Score: <span style={{ color: "green", fontSize: "15px", fontWeight: "bold" }}>
//                                                             {updatedCategory?.sets?.find(s => s.set === set.set)?.lastScore}
//                                                         </span>
//                                                     </span>
//                                                 </p>
//                                             )
//                                         }
//                                     </p>
//                                 </div>
//                             );
//                         }))
//                     }
//                 </div>
//             )}

//             {/* Step 3: Display Quiz Questions */}
//             {selectedSet && (
//                 <div>
//                     <div style={styles.filterContainer}>
//                         <button
//                             onClick={() => setShowOnly("All")}
//                             style={{
//                                 padding: '8px 16px',
//                                 borderRadius: '20px',
//                                 border: '1px solid #ddd',
//                                 background: showOnly === "All" ? '#007bff' : '#f5f5f5',
//                                 color: showOnly === "All" ? 'white' : '#333',
//                                 cursor: 'pointer',
//                                 fontSize: '14px'
//                             }}
//                         >
//                             All
//                         </button>
//                         <button
//                             onClick={() => {
//                                 // 1. Check if user is logged in
//                                 if (!user) {
//                                     alert("Please login to see marked questions!");
//                                     return;
//                                 }
//                                 // 2. Check if there are any important questions
//                                 if (importantQuestions.length === 0) {
//                                     alert("No marked questions found. Please mark questions (★) to see them here.");
//                                     return;
//                                 }
//                                 setShowOnly("Important")
//                             }}
//                             style={{
//                                 padding: '8px 16px',
//                                 borderRadius: '20px',
//                                 border: '1px solid #ddd',
//                                 background: showOnly === "Important" ? '#007bff' : '#f5f5f5',
//                                 color: showOnly === "Important" ? 'white' : '#333',
//                                 cursor: 'pointer',
//                                 fontSize: '14px'
//                             }}
//                         >
//                             Important ({importantQuestions.length})
//                         </button>
//                     </div>

//                     {/* NEW: Tag filter chips */}
//                     {availableTags.length > 1 && (
//                         <div style={{ ...styles.filterContainer, marginTop: '10px', marginBottom: '20px' }} className="hide-on-mobile">
//                             {availableTags.map((tag) => (
//                                 <button
//                                     key={tag}
//                                     onClick={() => setSelectedTag(tag)}
//                                     style={{
//                                         padding: '6px 14px',
//                                         borderRadius: '20px',
//                                         border: '1px solid #ddd',
//                                         background: selectedTag === tag ? '#28a745' : '#f5f5f5',
//                                         color: selectedTag === tag ? 'white' : '#333',
//                                         cursor: 'pointer',
//                                         fontSize: '13px',
//                                         transition: 'all 0.3s ease',
//                                     }}
//                                 >
//                                     {tag} {tag !== "All" && `(${shuffledQuestions.filter(q => q.tags?.includes(tag)).length})`}
//                                 </button>
//                             ))}
//                         </div>
//                     )}

//                     {/* NEW: Tag Search Container - EXACTLY like your implementation */}
//                     {availableTags.length > 1 && (
//                         <div style={styles.searchContainer} className="show-on-mobile">
//                             <input
//                                 type="text"
//                                 placeholder="Search topics/tags..."
//                                 value={tagSearchQuery}
//                                 onChange={(e) => {
//                                     setTagSearchQuery(e.target.value);
//                                     if (e.target.value.trim() === '') {
//                                         setShowTagRecommendations(true);
//                                     } else {
//                                         setShowTagRecommendations(false);
//                                     }
//                                 }}
//                                 onFocus={() => setShowTagRecommendations(true)}
//                                 onBlur={() => setTimeout(() => setShowTagRecommendations(false), 200)}
//                                 style={styles.searchInput}
//                             />
//                             {/* Tag recommendations */}
//                             {showTagRecommendations && filteredTags.length > 0 && (
//                                 <div style={styles.recommendationsContainer}>
//                                     {filteredTags.map((tag, index) => (
//                                         <div
//                                             key={index}
//                                             onClick={() => {
//                                                 setSelectedTag(tag);
//                                                 setTagSearchQuery(tag);
//                                                 setShowTagRecommendations(false);
//                                             }}
//                                             style={{
//                                                 ...styles.recommendationItem,
//                                                 ...(selectedTag === tag ? { backgroundColor: '#007bff', color: 'white' } : {})
//                                             }}
//                                             onMouseEnter={(e) => {
//                                                 e.target.style.backgroundColor = '#007bff';
//                                                 e.target.style.color = 'white';
//                                             }}
//                                             onMouseLeave={(e) => {
//                                                 if (selectedTag !== tag) {
//                                                     e.target.style.backgroundColor = '#fff';
//                                                     e.target.style.color = '#333';
//                                                 }
//                                             }}
//                                         >
//                                             {tag} {tag !== "All" && `(${shuffledQuestions.filter(q => q.tags?.includes(tag)).length})`}
//                                             {selectedTag === tag && ' ✓'}
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}
//                         </div>
//                     )}


//                     {questionsToShow.map((question, index) => (
//                         <div key={question.id} id={`question-${question.id}`} style={{ marginBottom: '20px' }}>
//                             <h3 style={{ whiteSpace: "pre-line" }}>{index + 1}. {question.question} <span style={{ fontWeight: "bold" }}>{question?.exam}</span>
//                                 <button
//                                     onClick={async () => {
//                                         if (!user) {
//                                             alert("Please login to mark questions!");
//                                             return;
//                                         }
//                                         // setImportantQuestions(prev =>
//                                         //     prev.includes(question.id)
//                                         //         ? prev.filter(id => id !== question.id)
//                                         //         : [...prev, question.id]
//                                         // );
//                                         // FIRST calculate the new state
//                                         const updatedImportantQuestions = importantQuestions.includes(question.id)
//                                             ? importantQuestions.filter(id => id !== question.id)
//                                             : [...importantQuestions, question.id];

//                                         // THEN update local state
//                                         setImportantQuestions(updatedImportantQuestions);

//                                         await saveImportantQuestions(updatedImportantQuestions);

//                                         // Automatically switch to "All" if no important questions left
//                                         if (updatedImportantQuestions.length === 0) {
//                                             setShowOnly("All");
//                                         }

//                                     }}
//                                     style={{
//                                         background: 'none',
//                                         border: 'none',
//                                         fontSize: '24px',
//                                         cursor: 'pointer',
//                                         color: importantQuestions.includes(question.id) ? 'gold' : '#ccc'
//                                     }}
//                                 >
//                                     {importantQuestions.includes(question.id) ? '★' : '☆'}
//                                 </button>
//                             </h3>
//                             <ul>
//                                 {question.options
//                                     .map((option) => (
//                                         <li
//                                             key={option}
//                                             onClick={() => handleOptionClick(question.id, option)}
//                                             style={{
//                                                 padding: '10px',
//                                                 margin: '5px 0',
//                                                 // cursor: 'pointer', ...getOptionStyle(question.id, option)
//                                                 cursor: selectedOptions[question.id] ? 'not-allowed' : 'pointer',
//                                                 ...getOptionStyle(question.id, option)
//                                             }}
//                                         >
//                                             {option}
//                                         </li>
//                                     ))}
//                             </ul>
//                             <div style={{ display: "flex", flexDirection: "row" }}>
//                                 <button
//                                     onClick={() => {
//                                         if (!user) {
//                                             alert("Please login to download the image!");
//                                             return;
//                                         }
//                                         handleShareClick(question)
//                                     }}
//                                     className="button"
//                                     style={{ fontSize: "small" }}

//                                 >
//                                     Image {<FaDownload size={10} />}
//                                 </button>
//                                 <button
//                                     onClick={() => {
//                                         if (!user) {
//                                             alert("Please login to download the video!");
//                                             return;
//                                         }
//                                         handleVideoShareClick(question)
//                                     }}
//                                     className="button"
//                                     style={{ marginLeft: "5px", fontSize: "small" }}
//                                 >
//                                     Video {<FaDownload size={10} />}
//                                 </button>
//                                 {/* Create a another explanation button to show explanation pop up */}

//                                 {/* Explanation Button (always visible but conditionally disabled) 
//                                 {selectedOptions[question.id] && question.explanation && (
//                                     <button
//                                         onClick={() => handleExplanationClick(question)}
//                                         className="button"
//                                         style={{ marginLeft: "5px", fontSize: "small" }}
//                                     >
//                                         Explain
//                                     </button>
//                                 )}
//                                 */}
//                                 {question.explanation && (
//                                     <button
//                                         onClick={() => {
//                                             if (!user) {
//                                                 alert("Please login to see the explanation!");
//                                                 return;
//                                             }
//                                             if (selectedOptions[question.id]) {
//                                                 handleExplanationClick(question);
//                                             } else {
//                                                 alert("Please select an option to see the explanation!");
//                                             }
//                                         }}
//                                         className="button"
//                                         style={{
//                                             marginLeft: "5px",
//                                             fontSize: "small",
//                                             cursor: selectedOptions[question.id] ? "pointer" : "not-allowed",
//                                             opacity: selectedOptions[question.id] ? 1 : 0.6,
//                                         }}
//                                     >
//                                         Explain
//                                     </button>
//                                 )}
//                             </div>

//                         </div>
//                     ))}

//                     {showOnly === "All" && selectedTag === "All" && (<button className="button" onClick={calculateScore}>Submit</button>)}
//                     {showScore && showOnly === "All" && (
//                         <div>
//                             <h2>Your Score: {score} out of {selectedSet.quizzes.length}</h2>
//                             <h2>Unattempted: {unattempted}</h2>
//                             <button className="button" onClick={handleRefresh}>Go Back</button>
//                         </div>
//                     )}
//                     {/* Render the SharePopup */}
//                     {showPopup && activeQuestion && (
//                         <SharePopup
//                             question={activeQuestion}
//                             onClose={() => setShowPopup(false)}
//                         />
//                     )}
//                     {/* Render the videoSharePopup */}
//                     {showVideoPopup && activeVideoQuestion && (
//                         <SharePopupWithVideo
//                             question={activeVideoQuestion}
//                             onClose={() => setShowVideoPopup(false)}
//                         />
//                     )}
//                     {/* Render the ExplanationPopup */}
//                     {showExplanationPopup && activeExplanationQuestion && (
//                         <ExplanationPopup
//                             question={activeExplanationQuestion}
//                             onClose={() => setShowExplanationPopup(false)}
//                         />
//                     )}

//                 </div>
//             )}
//         </div>
//     );
// }

// const styles = {
//     filterContainer: {
//         display: 'flex',
//         flexWrap: 'wrap',
//         gap: '10px',
//         marginBottom: '20px',
//     },
//     filterButton: {
//         padding: '8px 16px',
//         borderRadius: '20px',
//         border: '1px solid #ddd',
//         background: '#f5f5f5',
//         cursor: 'pointer',
//         fontSize: '14px',
//         transition: 'all 0.3s ease',
//     },
//     activeFilter: {
//         background: '#007bff',
//         color: 'white',
//         borderColor: '#007bff',
//     },
//     searchContainer: {
//         display: "flex",
//         // display: "none",
//         flexDirection: "column",
//         alignItems: "center",
//         position: "relative",
//         marginBottom: "20px",
//         width: "100%",
//     },
//     searchInput: {
//         padding: "12px",
//         width: "80%",
//         borderRadius: "8px",
//         border: "2px solid #007bff",
//         fontSize: "16px",
//         outline: "none",
//         transition: "0.3s",
//         boxShadow: "0 2px 5px rgba(0, 123, 255, 0.2)"
//     },
//     recommendationsContainer: {
//         position: "absolute",
//         width: "80%",
//         background: "#fff",
//         border: "1px solid #ddd",
//         borderRadius: "8px",
//         boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
//         marginTop: "50px",
//         zIndex: 10,
//         overflowY: "auto",  // Enables scrolling
//         maxHeight: "200px",  // Restricts max height        
//     },
//     recommendationItem: {
//         padding: "12px",
//         cursor: "pointer",
//         fontSize: "14px",
//         borderBottom: "1px solid #f0f0f0",
//         transition: "0.3s",
//         textAlign: "left",
//         color: "#333",
//         backgroundColor: "#fff"
//     },
//     recommendationItemHover: {
//         backgroundColor: "#007bff",
//         color: "#fff"
//     }
// };
// export default DummyPage;
